import { v4 as Uuid } from 'uuid'
import { assetDecodeManager } from '@/editor/media/AssetDecodeManager'
import { DEFAULT_DURATION, POSTPROCESSING_EFFECTS } from '@/etc/consts'
import { stripCanDrop, waitMs, blobFromUrl, jclone } from '@/etc/utils'
import { LOG } from '@/etc/log'
import { omit as _omit, pick as _pick } from 'lodash'
import supabase from '@/etc/supabase'
import { factory, tracksIndex } from './state'
import { fromSession, toSession } from '@/etc/storage'
import {
  DEFAULT_CAPTION_SENSITIVITY,
  segmentTranscript,
  sensitivityToGapSec,
} from '@/editor/captions/phraseSegmentation'
import { extractTranscriptionAudio } from '@/editor/audio/extractAudioForTranscription'
import { getTransform2dCanonicalProp, isTransform2dEnvelopeProp } from '@/editor/smart/transform2d'
import {
  getQuickbarZoomCanonicalProp,
  isQuickbarZoomEnvelopeProp,
} from '@/editor/smart/quickbarSmart'
import {
  buildTransformAgentState,
  buildTransformAtFrame,
  resolveTransformFrameRange,
} from '@/editor/agents/transformState'
import { resolveTransformMotionPreview } from '@/editor/agents/transformSpeed'
import {
  isTextNormalizedEffectControlProp,
  normalizeTextEffectControl,
} from '@/editor/text/textEffectControls'
import {
  applyTextClipStyle,
  isTextClipStyleProp,
  splitTextClipStyleUpdate,
} from '@/editor/text/textClipStyle'
import GLOBAL_EFFECT_PRESETS from '@/editor/effects/globalEffectPresets'
import GLOBAL_TEXT_MOTION_PRESETS from '@/editor/text/globalTextMotionPresets'
import { normalizeTextMotionPreset } from '@/editor/text/textMotionPresets'
import { createEffectUserPreset } from '@/editor/effects/effectPresets'
import {
  VYBY_PRESETS_TABLE,
  VYBY_PRESET_KIND_EFFECT,
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
  VYBY_PRESET_KIND_MOOD,
  VYBY_PRESET_KIND_NODE_WORKFLOW,
  VYBY_PRESET_KIND_PERSONALITY,
  VYBY_PRESET_KIND_TEXT_MOTION,
  normalizeVybyPresetRows,
  vybyPresetRowsToNodeWorkflowPresets,
  vybyPresetRowsToEffectPresets,
  vybyPresetRowsToMoodElements,
  vybyPresetRowsToPersonalityElements,
  vybyPresetRowsToTextMotionPresets,
} from '@/etc/vybyPresetCatalog'
import { buildCameraPresetCatalog } from '@/etc/cameraPresetCatalog'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/etc/analytics'
import { normalizeMoodboard } from '@/etc/projectMoodboard'
import {
  normalizeCameraProfile,
  normalizeCameraProfilePresetId,
} from '@/etc/cameraProfile'
import {
  createProjectGoal,
  hasDuplicateProjectGoal,
  normalizeProjectGoals,
} from '@/etc/projectGoals'

const log = LOG.m('store::project')
const { VUE_APP_AI_BACKEND, VUE_APP_S3 } = process.env
const AI_BACKEND_BASE = `${VUE_APP_AI_BACKEND || ''}`.trim().replace(/\/+$/, '')
const AUTOSAVE_DELAY_MS = 5000
const TRANSCRIBE_HTTP_TIMEOUT_MS = 10 * 60 * 1000

// Voice Overs Phase 2: session cache of provider voices ({ provider: [voice] })
// and the input-length guard (ElevenLabs Voice Changer caps input ~5 min).
const voiceoverVoicesCache = {}
const VOICEOVER_MAX_DURATION_SEC = 300
const NEW_PROJECT_PANEL_ENTRY_SESSION_KEY_PREFIX = 'ui/editor/newProjectPanelEntry/'

const socketAckErrorCode = (error) => {
  const message = `${error?.message || error || ''}`.toLowerCase()
  if (message.includes('timed out') || message.includes('timeout')) return 'SOCKET_TIMEOUT'
  if (
    message.includes('disconnect') ||
    message.includes('transport close') ||
    message.includes('not connected')
  ) {
    return 'SOCKET_DISCONNECTED'
  }
  return 'SOCKET_ERROR'
}

let autosaveTimer = null
let autosavePromise = null
let projectSettingsSliceSavePromise = null

const clearAutosaveTimer = () => {
  if (!autosaveTimer) return
  clearTimeout(autosaveTimer)
  autosaveTimer = null
}

const canAutosaveProject = (state) =>
  !!(
    state &&
    state.initialized &&
    state.uuid &&
    !state.isNew &&
    !state.saved &&
    !state.recording &&
    !state.calculating
  )

const autosaveErrorMessage = (err) =>
  `${err?.message || err?.error_description || err || 'Autosave failed'}`

const getStripRetimeAnchor = (store, tuuid, suuid) => {
  const anchor = store.state.tracks?.[tuuid]?.strips?.[suuid]?.retime?.anchor
  return anchor === 'front' ? 'front' : 'back'
}

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const finiteNum = (value, fallback = null) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const finitePositive = (value, fallback = null) => {
  const n = finiteNum(value, null)
  return n != null && n > 0 ? n : fallback
}

const finiteNonNegative = (value, fallback = null) => {
  const n = finiteNum(value, null)
  return n != null && n >= 0 ? n : fallback
}

const normalizeAssetSource = (value, fallback = 'upload') => {
  const source = `${value || ''}`.trim().toLowerCase()
  if (source === 'generation') return 'generation'
  if (source === 'upload') return 'upload'
  if (source === 'global' || source === 'linked') return 'global'
  return fallback
}

const normalizeMediaPayload = (payload) => {
  if (payload && typeof payload === 'object' && payload.file) {
    return {
      file: payload.file,
      uploadNew: !!payload.uploadNew,
      source: normalizeAssetSource(payload.source, 'upload'),
      meta: payload.meta && typeof payload.meta === 'object' ? payload.meta : {},
      markDirty: payload.markDirty !== false,
    }
  }

  return {
    file: payload,
    uploadNew: false,
    source: 'upload',
    meta: {},
    markDirty: true,
  }
}

const projectFileMeta = (file, source = 'upload', addedAt = null) => ({
  hearted: !!file?.hearted,
  path: file?.path || '',
  persisted: true,
  source: normalizeAssetSource(source, 'upload'),
  addedAt: addedAt || null,
  name: file?.path ? `${file.path}`.split('/').pop() : '',
  // Proxy video: seek-dense proxy transcoded by the backend (Proxy_Video.md).
  proxyPath: file?.proxy_path || null,
})

// Proxy video Phase 2 detection thresholds (Proxy_Video.md): proxy-worthy when
// the media has at most one keyframe (every mid-clip seek re-decodes from
// frame 0), or is long-form with sparse keyframes.
const PROXY_MIN_DURATION_SEC = 20
const PROXY_SPARSE_GOP_SEC = 3

// Hydration must not complete before assets are actually usable: addMedia
// returns as soon as the blob is registered, while e.g. VideoAsset keeps
// preparing (dimensions + thumbnail) and only then emits 'ready'. Attaching
// clips in that window builds broken geometry and blank thumbnails.
const HYDRATE_READY_TIMEOUT_MS = 30000

const waitForAssetReady = (asset, timeoutMs = HYDRATE_READY_TIMEOUT_MS) =>
  new Promise((resolve) => {
    if (!asset) return resolve({ ready: false, reason: 'no-asset' })
    if (asset._asset?.ready) return resolve({ ready: true })
    let settled = false
    const finish = (result) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      asset.off('ready', onReady)
      asset.off('asset.error', onError)
      resolve(result)
    }
    const onReady = () => finish({ ready: true })
    const onError = (err) => finish({ ready: false, reason: 'error', err })
    const timer = setTimeout(
      () => finish({ ready: false, reason: 'timeout' }),
      timeoutMs
    )
    asset.once('ready', onReady)
    asset.once('asset.error', onError)
  })

const collectTimelineAssetIds = (tracks = {}) => {
  const ids = new Set()
  Object.values(tracks || {}).forEach((track) => {
    Object.values(track?.strips || {}).forEach((strip) => {
      const uuid =
        typeof strip?.asset === 'string'
          ? strip.asset
          : strip?.asset?.uuid
      if (uuid) ids.add(uuid)
    })
  })
  return ids
}

const sortProjectFilesForHydration = (files = [], tracks = {}) => {
  const timelineIds = collectTimelineAssetIds(tracks)
  return [...files].sort((a, b) => {
    const aTimeline = timelineIds.has(a?.id) ? 0 : 1
    const bTimeline = timelineIds.has(b?.id) ? 0 : 1
    if (aTimeline !== bTimeline) return aTimeline - bTimeline
    return `${a?.id || ''}`.localeCompare(`${b?.id || ''}`)
  })
}

const assetSourceMapFromLibrary = (library = {}, files = []) => {
  const allowed = new Set(files)
  return Object.values(library || {}).reduce((acc, item) => {
    const uuid = `${item?.uuid || ''}`.trim()
    if (!uuid || (allowed.size && !allowed.has(uuid))) return acc
    acc[uuid] = normalizeAssetSource(item?.meta?.source, 'upload')
    return acc
  }, {})
}

const assetAddedAtMapFromLibrary = (library = {}, files = []) => {
  const allowed = new Set(files)
  return Object.values(library || {}).reduce((acc, item) => {
    const uuid = `${item?.uuid || ''}`.trim()
    const addedAt = `${item?.meta?.addedAt || ''}`.trim()
    if (!uuid || !addedAt || (allowed.size && !allowed.has(uuid))) return acc
    acc[uuid] = addedAt
    return acc
  }, {})
}

const derivedClipMetaMapFromLibrary = (library = {}, files = []) => {
  const allowed = new Set(files)
  return Object.values(library || {}).reduce((acc, item) => {
    const uuid = `${item?.uuid || ''}`.trim()
    const derivedClip = item?.meta?.derivedClip
    if (
      !uuid ||
      !derivedClip ||
      typeof derivedClip !== 'object' ||
      (allowed.size && !allowed.has(uuid))
    ) {
      return acc
    }
    acc[uuid] = {
      persistGenerated: !!item.meta.persistGenerated,
      derivedClip: jclone(derivedClip),
      promptDraft:
        item.meta.promptDraft && typeof item.meta.promptDraft === 'object'
          ? jclone(item.meta.promptDraft)
          : null,
    }
    return acc
  }, {})
}

const shouldUploadLibraryAsset = (libraryItem, existingProjectFileIds) => {
  const source = normalizeAssetSource(libraryItem?.meta?.source, 'upload')
  const isPersistedGeneration =
    source === 'generation' && !!libraryItem?.meta?.persistGenerated
  if (source !== 'upload' && !isPersistedGeneration) return false
  if (libraryItem?.meta?.persisted) return false
  return !existingProjectFileIds.includes(libraryItem?.uuid)
}

const stripUsesAsset = (strip, uuid) => {
  const auuid =
    typeof strip?.asset === 'string'
      ? strip.asset
      : strip?.asset?.uuid
  return `${auuid || ''}` === `${uuid || ''}`
}

const projectUsesAsset = (tracks = {}, uuid) =>
  Object.values(tracks || {}).some((track) =>
    Object.values(track?.strips || {}).some((strip) => stripUsesAsset(strip, uuid))
  )

const filePathFromGeneratedOut = (out = {}) => {
  const path =
    out.filePath ||
    out.file_path ||
    out.path ||
    out?.out?.s3 ||
    out?.out?.filePath ||
    out?.out?.file_path ||
    ''
  return `${path || ''}`.trim()
}

const effectDropDebugEnabled = () =>
  typeof window !== 'undefined' && window.__VYBY_DEBUG_EFFECT_DROP === true

const debugEffectDrop = (label, payload = undefined) => {
  if (!effectDropDebugEnabled()) return
  if (typeof payload === 'undefined') {
    console.debug('[effect-drop]', label)
    return
  }
  console.debug('[effect-drop]', label, payload)
}

const toSecondsFromFrames = (frames, fps, fallback = null) => {
  const frameCount = finiteNonNegative(frames, null)
  const f = finitePositive(fps, null)
  if (frameCount == null || f == null) return fallback
  return frameCount / f
}

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const s = value.trim()
    if (s) return s
  }
  return ''
}

const mergeGlobalPresetLists = (fallback = [], cms = []) => {
  const out = []
  const index = new Map()
  ;[...(fallback || []), ...(cms || [])].forEach((preset) => {
    if (!preset) return
    const id = firstNonEmptyString(preset.id, preset.code)
    if (!id) return
    if (index.has(id)) {
      out.splice(index.get(id), 1, preset)
      return
    }
    index.set(id, out.length)
    out.push(preset)
  })
  return out
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const DEFAULT_PROJECT_TITLE_PREFIX = 'Name your project '

const normalizeProjectBanner = (banner) => {
  if (!banner || typeof banner !== 'object') return null
  const id = firstNonEmptyString(banner.id)
  const path = firstNonEmptyString(banner.path)
  const mime = firstNonEmptyString(banner.mime)
  if (!id && !path) return null
  return { id, path, mime, explicit: banner.explicit === true }
}

const pickDefaultProjectBanner = (fileIds = [], fileRows = []) => {
  const rowsById = (fileRows || []).reduce((acc, row) => {
    if (row?.id) acc[row.id] = row
    return acc
  }, {})

  for (const id of fileIds || []) {
    const row = rowsById[id]
    const banner = normalizeProjectBanner(row)
    if (banner) return banner
  }
  return null
}

const getNextDefaultProjectTitle = async (store) => {
  const fallback = Object.keys(store.rootState.user?.projects || {}).length + 1
  if (!store.rootState.user?.uid) return `${DEFAULT_PROJECT_TITLE_PREFIX}${fallback}`

  const { data, error } = await supabase
    .from('projects')
    .select('name')
    .eq('owner', store.rootState.user.uid)

  if (error || !Array.isArray(data)) {
    return `${DEFAULT_PROJECT_TITLE_PREFIX}${fallback}`
  }

  const max = data.reduce((acc, project) => {
    const name = `${project?.name || ''}`.trim()
    const match = name.match(/^Name your project\s+(\d+)$/i)
    return match ? Math.max(acc, Number(match[1]) || 0) : acc
  }, 0)

  return `${DEFAULT_PROJECT_TITLE_PREFIX}${max + 1}`
}

const findStripLocation = (state, stripId) => {
  if (!stripId) return null
  const tuuid = Object.keys(state.tracks || {}).find(
    (id) => !!state.tracks?.[id]?.strips?.[stripId]
  )
  if (!tuuid) return null
  const strip = state.tracks?.[tuuid]?.strips?.[stripId] || null
  if (!strip) return null
  return { tuuid, strip }
}

const layerTrackIds = (state) =>
  Object.keys(state.tracks || {})
    .filter((tuuid) => state.tracks?.[tuuid]?.type === 'layer')
    .sort((a, b) => {
      const al = finiteNum(state.tracks?.[a]?.layer, 0)
      const bl = finiteNum(state.tracks?.[b]?.layer, 0)
      return al - bl
    })

const pickTextCreateTrackId = (state) => {
  const activeTrack = state.timeline?.activeTrack || null
  if (activeTrack && state.tracks?.[activeTrack]?.type === 'layer') return activeTrack
  const layers = layerTrackIds(state)
  return layers.length ? layers[0] : null
}

const pickInsertDepth = ({ state, tuuid, time, length }) => {
  const track = state.tracks?.[tuuid]
  if (!track) return 0
  const strips = Object.values(track.strips || {})
  let depth = Math.max(0, finiteNum(track.maxDepth, 0))
  for (let d = depth; d < depth + 20; d++) {
    const ok = !strips.some((s) => s && !stripCanDrop(d, time, length, s))
    if (ok) {
      depth = d
      break
    }
  }
  return depth
}

// Captions Phase 5a: bottom-up lane scan for caption staggering. Unlike
// pickInsertDepth (which starts at maxDepth and inserts above everything),
// this prefers the LOWEST free lane from the track base, so overlapping
// caption animation windows alternate above/below instead of stacking ever
// higher. Returns null when no lane within the cap is free.
const pickCaptionLane = ({ state, tuuid, time, length, baseDepth = 0, maxLanes = 3 }) => {
  const track = state.tracks?.[tuuid]
  if (!track) return null
  const strips = Object.values(track.strips || {})
  for (let d = baseDepth; d < baseDepth + maxLanes; d++) {
    const ok = !strips.some((s) => s && !stripCanDrop(d, time, length, s))
    if (ok) return d
  }
  return null
}

const extractTextFromTextAnimationOperations = (operations = []) => {
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'content' || action !== 'upsert') continue
    if (typeof op.payload === 'string') {
      const txt = op.payload.trim()
      if (txt) return txt
      continue
    }
    if (!isPlainObject(op.payload)) continue
    const fromFields = firstNonEmptyString(
      op.payload.text,
      op.payload.content,
      op.payload.value,
      op.payload.string,
      op.payload.label
    )
    if (fromFields) return fromFields
    if (Array.isArray(op.payload.lines)) {
      const lines = op.payload.lines
        .map((x) => (typeof x === 'string' ? x.trim() : ''))
        .filter((x) => !!x)
      if (lines.length) return lines.join('\n')
    }
  }
  return ''
}

const extractTextTimingFromOperations = (operations = [], fpsFallback = 24) => {
  let startSec = null
  let durationSec = null
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'timing' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const p = op.payload
    const opFps = finitePositive(p.fps, fpsFallback)
    if (startSec == null) {
      startSec = finiteNonNegative(
        p.startSec ?? p.startTimeSec ?? p.timeSec,
        toSecondsFromFrames(p.startFrame, opFps, null)
      )
    }
    if (durationSec == null) {
      durationSec = finitePositive(
        p.durationSec ?? p.lengthSec ?? p.spanSec,
        toSecondsFromFrames(
          p.durationFrames ?? p.lengthFrames ?? p.spanFrames,
          opFps,
          null
        )
      )
    }
  }
  return { startSec, durationSec }
}

const extractTextCreateStartSec = ({
  payload,
  timelinePlayheadSec = 0,
  projectFps = 24,
}) => {
  const create = isPlainObject(payload?.create) ? payload.create : {}
  const anchor = isPlainObject(create.anchor) ? create.anchor : {}
  const fps = finitePositive(create.fps, projectFps)
  const fromSec = finiteNonNegative(
    anchor.playheadSec ?? create.playheadSec,
    null
  )
  if (fromSec != null) return fromSec
  const fromFrame = finiteNonNegative(
    anchor.playheadFrame ?? create.playheadFrame,
    null
  )
  if (fromFrame != null) return toSecondsFromFrames(fromFrame, fps, timelinePlayheadSec)
  return finiteNonNegative(timelinePlayheadSec, 0)
}

const extractTextCreateDurationSec = ({ payload, operations = [], projectFps = 24 }) => {
  const create = isPlainObject(payload?.create) ? payload.create : {}
  const fps = finitePositive(create.fps, projectFps)
  const fromOps = extractTextTimingFromOperations(operations, fps).durationSec
  return finitePositive(
    create.durationSec ?? create.lengthSec ?? payload.durationSec ?? payload.lengthSec,
    finitePositive(
      toSecondsFromFrames(
        create.durationFrames ??
          create.lengthFrames ??
          payload.durationFrames ??
          payload.lengthFrames,
        fps,
        null
      ),
      finitePositive(fromOps, DEFAULT_DURATION)
    )
  )
}

const TEXT_ANIMATION_AGENT_WRITE_MODES = new Set([
  'merge_text_animation_ops',
  'replace_text_animation_ops_in_range',
  'replace_all_text_animation_ops',
  'append_text_animation_ops',
])

const TEXT_ANIMATION_OPERATION_TARGETS = new Set([
  'content',
  'timing',
  'style',
  'animation',
])

const TEXT_ANIMATION_STYLE_PROPS = new Set([
  'font.family',
  'font.size',
  'font.weight',
  'font.style',
  'font.color',
  'font.lineHeight',
  'font.letterSpacing',
  'font.align',
  'effect.outlineWidth',
  'effect.outlineColor',
  'effect.shadowColor',
  'effect.shadowBlur',
  'effect.shadowOffsetX',
  'effect.shadowOffsetY',
])

const TEXT_ANIMATION_STYLE_ALIASES = {
  fontFamily: 'font.family',
  family: 'font.family',
  fontSize: 'font.size',
  size: 'font.size',
  fontWeight: 'font.weight',
  weight: 'font.weight',
  fontStyle: 'font.style',
  style: 'font.style',
  color: 'font.color',
  fontColor: 'font.color',
  lineHeight: 'font.lineHeight',
  letterSpacing: 'font.letterSpacing',
  tracking: 'font.letterSpacing',
  align: 'font.align',
  alignment: 'font.align',
  outlineWidth: 'effect.outlineWidth',
  outlineColor: 'effect.outlineColor',
  shadowColor: 'effect.shadowColor',
  shadowBlur: 'effect.shadowBlur',
  shadowOffsetX: 'effect.shadowOffsetX',
  shadowOffsetY: 'effect.shadowOffsetY',
}

const TEXT_ANIMATION_NUMBER_STYLE_PROPS = new Set([
  'font.size',
  'font.weight',
  'font.lineHeight',
  'font.letterSpacing',
  'effect.outlineWidth',
  'effect.shadowBlur',
  'effect.shadowOffsetX',
  'effect.shadowOffsetY',
])

const TEXT_ANIMATION_SMART_PROPS = new Set([
  'position.x',
  'position.y',
  'scale.x',
  'scale.y',
  'rotation.z',
  'opacity',
])

const TEXT_ANIMATION_REGIONS = ['entry', 'sustain', 'exit']

const validateTextAnimationAgentOperations = (operations = []) => {
  if (!Array.isArray(operations)) return { ok: false, reason: 'invalid_operations' }
  for (const op of operations) {
    if (!isPlainObject(op)) return { ok: false, reason: 'invalid_operation' }
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || 'upsert'}`.trim().toLowerCase()
    if (!TEXT_ANIMATION_OPERATION_TARGETS.has(target)) {
      return { ok: false, reason: 'unsupported_text_animation_target', target }
    }
    if (!['upsert', 'remove'].includes(action)) {
      return { ok: false, reason: 'unsupported_text_animation_action', action }
    }
  }
  return { ok: true }
}

const resolveTextAnimationRangeSeconds = ({ payload, strip, fps }) => {
  const writeMode = `${payload?.writeMode || ''}`.trim()
  if (writeMode !== 'replace_text_animation_ops_in_range') {
    return { ok: true, range: null }
  }
  const range = payload?.range
  if (!isPlainObject(range)) {
    return { ok: false, reason: 'missing_text_animation_range', missing: ['range'] }
  }

  const clipFrameCount = Math.max(1, Math.round(finiteNum(strip?.length, 0) * fps))
  const startRaw = finiteNum(range.startFrame, null)
  const endRaw = finiteNum(range.endFrameExclusive, null)
  if (startRaw == null || endRaw == null) {
    return {
      ok: false,
      reason: 'invalid_text_animation_range',
      missing: ['range.startFrame', 'range.endFrameExclusive'],
    }
  }

  const startFrame = Math.max(0, Math.min(clipFrameCount - 1, Math.round(startRaw)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(clipFrameCount, Math.round(endRaw))
  )

  return {
    ok: true,
    range: {
      startFrame,
      endFrameExclusive,
      startSec: startFrame / fps,
      endSec: endFrameExclusive / fps,
      spanSec: (endFrameExclusive - startFrame) / fps,
    },
  }
}

const normalizeTextStyleProp = (prop) => {
  const raw = `${prop || ''}`.trim()
  if (!raw) return ''
  return TEXT_ANIMATION_STYLE_ALIASES[raw] || raw
}

const normalizeTextStyleValue = (prop, value) => {
  if (isTextNormalizedEffectControlProp(prop)) {
    const num = finiteNum(value, null)
    return num == null ? undefined : normalizeTextEffectControl(prop, num)
  }
  if (TEXT_ANIMATION_NUMBER_STYLE_PROPS.has(prop)) {
    const num = finiteNum(value, null)
    return num == null ? undefined : num
  }
  if (typeof value === 'string') {
    const next = value.trim()
    return next ? next : undefined
  }
  return typeof value === 'number' || typeof value === 'boolean' ? value : undefined
}

const extractTextStyleOperations = (operations = []) => {
  const updates = {}
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'style' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const source = isPlainObject(op.payload.values)
      ? op.payload.values
      : isPlainObject(op.payload.style)
        ? op.payload.style
        : op.payload
    Object.entries(source).forEach(([key, value]) => {
      const prop = normalizeTextStyleProp(key)
      if (!TEXT_ANIMATION_STYLE_PROPS.has(prop)) return
      const next = normalizeTextStyleValue(prop, value)
      if (typeof next !== 'undefined') updates[prop] = next
    })
  }
  return updates
}

const textSmartDefaultValue = (prop, fallback = null) => {
  if (typeof fallback === 'number') return fallback
  if (prop === 'opacity') return 1
  if (prop.startsWith('scale.')) return 1
  return 0
}

const offsetToSeconds = (value, fps, fallback = 0) => {
  if (isPlainObject(value)) {
    const unit = `${value.unit || 'seconds'}`.trim().toLowerCase()
    const amount = finiteNum(value.value, null)
    if (amount == null) return fallback
    if (unit === 'frames') return amount / fps
    if (unit === 'beats') return amount * (60 / 120)
    return amount
  }
  return finiteNonNegative(value, fallback)
}

const beatsForTextRate = (micro = {}) => {
  const unit = `${micro.rateUnit || ''}`.trim().toLowerCase()
  const base = unit === 'sixteenth'
    ? 0.25
    : unit === 'eighth'
      ? 0.5
      : unit === 'half'
        ? 2
        : unit === 'whole'
          ? 4
          : 1
  const dotted = micro.dotted ? 3 / 2 : 1
  const triplet = micro.triplet ? 2 / 3 : 1
  return base * dotted * triplet
}

const normalizeTextMicro = (micro = {}) => {
  if (!isPlainObject(micro)) return null
  const next = {
    ...micro,
    shape: micro.shape || 'windowRelative',
    amp: finiteNum(micro.amp, 1),
    yoyo: typeof micro.yoyo === 'boolean' ? micro.yoyo : true,
    beatLocked: typeof micro.beatLocked === 'boolean' ? micro.beatLocked : true,
    beatLockScope: ['global', 'local'].includes(micro.beatLockScope)
      ? micro.beatLockScope
      : 'global',
    rateUnit: micro.rateUnit || 'quarter',
    dotted: !!micro.dotted,
    triplet: !!micro.triplet,
    phaseBeats: finiteNum(micro.phaseBeats, 0),
  }
  const beats = beatsForTextRate(next)
  next.every = isPlainObject(micro.every) ? micro.every : { unit: 'beats', value: beats }
  next.span = isPlainObject(micro.span) ? micro.span : { unit: 'beats', value: beats }
  if (isPlainObject(micro.window)) {
    next.window = {
      low: finiteNum(micro.window.low, 0),
      high: finiteNum(micro.window.high, 0),
    }
  } else if (Number.isFinite(Number(micro.radius))) {
    const radius = Math.abs(Number(micro.radius))
    next.window = { low: -radius, high: radius }
  }
  return next
}

const normalizeTextMicroByRegion = (microByRegion = {}) => {
  if (!isPlainObject(microByRegion)) return null
  return TEXT_ANIMATION_REGIONS.reduce((acc, region) => {
    const micro = normalizeTextMicro(microByRegion[region])
    if (micro) acc[region] = { ...micro, region }
    return acc
  }, {})
}

const normalizeTextSmartPart = (value, fallback) => {
  const source = isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, 'value')
    ? value.value
    : value
  const next = finiteNum(source, null)
  return { value: next == null ? fallback : next }
}

const normalizeTextSmartGroup = ({
  prop,
  group,
  existing = null,
  baseValue = null,
  rangeSeconds = null,
  stripLength = 0,
  fps = 24,
} = {}) => {
  if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return null
  const fallback = textSmartDefaultValue(prop, baseValue)
  const current = isPlainObject(existing) ? existing : {}
  const next = {
    start: normalizeTextSmartPart(group.start ?? current.start, fallback),
    left: normalizeTextSmartPart(group.left ?? current.left, fallback),
    right: normalizeTextSmartPart(group.right ?? current.right, fallback),
    end: normalizeTextSmartPart(group.end ?? current.end, fallback),
    offsets: {
      entry: offsetToSeconds(
        group.offsets?.entry ?? group.entryOffset ?? group.entryOffsetSec,
        fps,
        rangeSeconds ? rangeSeconds.startSec : finiteNonNegative(current.offsets?.entry, 0)
      ),
      exit: offsetToSeconds(
        group.offsets?.exit ?? group.exitOffset ?? group.exitOffsetSec,
        fps,
        rangeSeconds
          ? Math.max(0, stripLength - rangeSeconds.endSec)
          : finiteNonNegative(current.offsets?.exit, 0)
      ),
    },
    links: isPlainObject(group.links)
      ? group.links
      : isPlainObject(current.links)
        ? current.links
        : { rightToLeft: false },
    entryEasing: group.entryEasing || current.entryEasing || 'Linear.None',
    sustainEasing: group.sustainEasing || current.sustainEasing || 'Linear.None',
    exitEasing: group.exitEasing || current.exitEasing || 'Linear.None',
  }
  const microByRegion = normalizeTextMicroByRegion({
    ...(current.microByRegion || {}),
    ...(group.microByRegion || {}),
  })
  if (microByRegion && Object.keys(microByRegion).length) {
    next.microByRegion = microByRegion
  }
  return next
}

const textMotionPresetById = (presetId, globalPresets = GLOBAL_TEXT_MOTION_PRESETS) => {
  const id = `${presetId || ''}`.trim()
  if (!id) return null
  return (globalPresets || []).find((preset) => preset?.id === id) || null
}

const textPresetPartValue = (part = {}, prop, scaleUnit = 'raw') => {
  if (prop === 'position.x') return part.position?.x
  if (prop === 'position.y') return part.position?.y
  if (prop === 'scale.x') {
    const value = finiteNum(part.scale?.x, null)
    if (value == null) return undefined
    return scaleUnit === 'percent' ? value / 100 : value
  }
  if (prop === 'scale.y') {
    const value = finiteNum(part.scale?.y, null)
    if (value == null) return undefined
    return scaleUnit === 'percent' ? value / 100 : value
  }
  if (prop === 'rotation.z') return part.rotation?.z
  return undefined
}

const textPresetGroupFromParts = ({ preset, prop }) => {
  if (!preset?.parts || !TEXT_ANIMATION_SMART_PROPS.has(prop)) return null
  const fallback = textSmartDefaultValue(prop)
  const group = {}
  ;['start', 'left', 'right', 'end'].forEach((partName) => {
    const value = finiteNum(
      textPresetPartValue(preset.parts?.[partName], prop, preset.scaleUnit),
      null
    )
    if (value == null) return
    if (Math.abs(value - fallback) > 1e-9) group[partName] = { value }
  })
  if (preset.offsets) {
    const entry = finiteNonNegative(preset.offsets.entry, 0)
    const exit = finiteNonNegative(preset.offsets.exit, 0)
    const offsets = {}
    if (entry > 1e-9) offsets.entry = entry
    if (exit > 1e-9) offsets.exit = exit
    if (Object.keys(offsets).length) group.offsets = offsets
  }
  return Object.keys(group).length ? group : null
}

const textPresetGroupDelta = (prop, group = {}) => {
  if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return null
  const fallback = textSmartDefaultValue(prop)
  const delta = {}
  ;['start', 'left', 'right', 'end'].forEach((partName) => {
    const value = finiteNum(group?.[partName]?.value ?? group?.[partName], null)
    if (value == null) return
    if (Math.abs(value - fallback) > 1e-9) delta[partName] = { value }
  })
  const entry = finiteNonNegative(group.offsets?.entry, 0)
  const exit = finiteNonNegative(group.offsets?.exit, 0)
  const offsets = {}
  if (entry > 1e-9) offsets.entry = entry
  if (exit > 1e-9) offsets.exit = exit
  if (Object.keys(offsets).length) delta.offsets = offsets
  if (group.entryEasing && entry > 1e-9) delta.entryEasing = group.entryEasing
  if (group.sustainEasing) delta.sustainEasing = group.sustainEasing
  if (group.exitEasing && exit > 1e-9) delta.exitEasing = group.exitEasing
  if (isPlainObject(group.links)) delta.links = cloneJson(group.links) || group.links
  if (isPlainObject(group.microByRegion)) {
    delta.microByRegion = cloneJson(group.microByRegion) || group.microByRegion
  }
  return Object.keys(delta).length ? delta : null
}

const textAnimationGroupsFromPreset = (payload = {}, globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS) => {
  const presetId = firstNonEmptyString(payload.presetId, payload.preset, payload.id)
  const preset = normalizeTextMotionPreset(
    textMotionPresetById(presetId, globalTextMotionPresets)
  )
  if (!preset) return null

  const groups = {}
  Object.entries(preset.props || {}).forEach(([prop, group]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
    const delta = textPresetGroupDelta(prop, group)
    if (delta) groups[prop] = delta
  })

  ;(preset.supportedProps || []).forEach((prop) => {
    if (groups[prop]) return
    const group = textPresetGroupFromParts({ preset, prop })
    if (group) groups[prop] = group
  })

  Object.entries(preset.microByRegion || {}).forEach(([prop, microByRegion]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(microByRegion)) return
    groups[prop] = {
      ...(groups[prop] || {}),
      microByRegion: cloneJson(microByRegion) || microByRegion,
    }
  })

  return Object.keys(groups).length ? groups : null
}

const extractTextAnimationOperationSource = (
  payload = {},
  globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS
) => {
  if (!isPlainObject(payload)) return null
  const presetGroups = textAnimationGroupsFromPreset(payload, globalTextMotionPresets)
  if (presetGroups) return presetGroups
  if (isPlainObject(payload.props)) return payload.props
  if (isPlainObject(payload.smartKeys)) return payload.smartKeys
  if (isPlainObject(payload.groups)) return payload.groups
  if (payload.prop && isPlainObject(payload.group)) return { [payload.prop]: payload.group }
  return payload
}

const extractTextAnimationGroups = ({
  operations = [],
  strip,
  asset,
  writeMode,
  rangeSeconds = null,
  fps = 24,
  globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS,
} = {}) => {
  const groups = {}
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'animation' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const source = extractTextAnimationOperationSource(
      op.payload,
      globalTextMotionPresets
    )
    if (!isPlainObject(source)) continue
    Object.entries(source).forEach(([prop, group]) => {
      if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
      const baseValues = {
        ...(asset?.defaultProps || {}),
        ...(strip?.animation?.values || {}),
      }
      const existing =
        writeMode === 'replace_all_text_animation_ops'
          ? null
          : groups[prop] || strip?.smartKeys?.[prop] || null
      const normalized = normalizeTextSmartGroup({
        prop,
        group,
        existing,
        baseValue: baseValues[prop],
        rangeSeconds,
        stripLength: finitePositive(strip?.length, DEFAULT_DURATION),
        fps,
      })
      if (normalized) groups[prop] = normalized
    })
  }
  return groups
}

const applyTextStyleUpdates = ({ strip, asset, updates }) => {
  if (!strip || !asset || !isPlainObject(updates) || !Object.keys(updates).length) {
    return false
  }
  if (!strip.animation || typeof strip.animation !== 'object') return false
  strip.animation.values = {
    ...(strip.animation.values || {}),
    ...updates,
  }
  if (Array.isArray(strip.animation.keyframes)) {
    strip.animation.keyframes = strip.animation.keyframes.map((kf) => {
      if (!kf || typeof kf !== 'object') return kf
      const next = {
        ...kf,
        props: { ...(kf.props || {}) },
        delay: { ...(kf.delay || {}) },
        easing: { ...(kf.easing || {}) },
      }
      Object.entries(updates).forEach(([prop, value]) => {
        if (isTextClipStyleProp(prop)) return
        if (typeof next.props[prop] === 'undefined' && prop !== 'font.size') return
        next.props[prop] = value
        if (typeof next.delay[prop] === 'undefined') next.delay[prop] = 0
        if (typeof next.easing[prop] === 'undefined') next.easing[prop] = 'Linear.None'
      })
      return next
    })
  }
  applyTextClipStyle(strip.animation, splitTextClipStyleUpdate(updates).clip)
  Object.entries(updates).forEach(([prop, value]) => {
    if (typeof asset.pset === 'function') asset.pset(prop, value)
  })
  if (typeof asset.emit === 'function') {
    asset.emit('updateAnimation', strip.animation)
    asset.emit('props', {
      ...(asset.lastProps || {}),
      ...updates,
    })
  }
  return true
}

const applyTextAnimationGroups = ({ store, tuuid, suuid, groups }) => {
  if (!store || !tuuid || !suuid || !isPlainObject(groups)) return 0
  let applied = 0
  Object.entries(groups).forEach(([prop, group]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
    store.commit('upsertSmartKeys', {
      tuuid,
      suuid,
      prop,
      group,
    })
    applied += 1
  })
  return applied
}

const normalizeEffectCode = (value) => {
  const code = `${value || ''}`.trim()
  return code || ''
}

const effectItemMath = (effectItem) => {
  if (typeof effectItem?.math === 'string' && effectItem.math.trim()) {
    return effectItem.math.trim()
  }
  if (
    isPlainObject(effectItem?.controls) &&
    typeof effectItem.controls.math === 'string' &&
    effectItem.controls.math.trim()
  ) {
    return effectItem.controls.math.trim()
  }
  return ''
}

const effectMatchesAgentItem = ({ effect = null, effectCode = null, math = null } = {}) => {
  if (!effect) return false
  const code = normalizeEffectCode(effectCode).toLowerCase()
  if (!code || `${effect.code || ''}`.trim().toLowerCase() !== code) return false
  const effectMath = `${math || ''}`.trim()
  if (code === 'postprocessing' && effectMath) {
    return `${effect.math || ''}`.trim() === effectMath
  }
  return true
}

const resolveStripEffectIds = ({ strip, effectId = null, effectCode = null, math = null }) => {
  if (!strip || !isPlainObject(strip.effects)) return []
  const explicitId = `${effectId || ''}`.trim()
  if (explicitId && strip.effects[explicitId]) return [explicitId]
  const code = normalizeEffectCode(effectCode)
  if (!code) return []
  return Object.entries(strip.effects)
    .filter(([, eff]) => effectMatchesAgentItem({ effect: eff, effectCode: code, math }))
    .map(([id]) => id)
}

const resolveStripEffectId = ({
  strip,
  effectId = null,
  effectCode = null,
  math = null,
}) => {
  const ids = resolveStripEffectIds({ strip, effectId, effectCode, math })
  return ids.length ? ids[0] : null
}

const EFFECTS_AGENT_WRITE_MODES = new Set([
  'merge_effect_controls',
  'replace_effect_controls_in_range',
  'replace_all_effect_controls',
  'append_effect_controls',
])

const EFFECTS_AGENT_CONTROL_GROUPS = ['values', 'auto', 'smart', 'mod']

const validateEffectsAgentItem = (effectItem) => {
  if (!isPlainObject(effectItem)) {
    return { ok: false, reason: 'invalid_effect_item' }
  }

  const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
  if (!['upsert', 'remove'].includes(action)) {
    return { ok: false, reason: 'unsupported_effect_action', action }
  }

  const effectCode = normalizeEffectCode(effectItem.effectCode).toLowerCase()
  const explicitId = `${effectItem.effectId || ''}`.trim()
  if (!effectCode && !explicitId) {
    return { ok: false, reason: 'missing_effect_target' }
  }
  if (action === 'remove') return { ok: true }
  if (effectCode !== 'postprocessing') {
    return { ok: false, reason: 'unsupported_effect_code', effectCode }
  }

  const math = effectItemMath(effectItem)
  const style = math ? POSTPROCESSING_EFFECTS?.[math] : null
  if (!style) {
    return { ok: false, reason: 'unsupported_postprocessing_style', math }
  }

  const validParams = new Set(Object.keys(style.controls || {}))
  const controls = isPlainObject(effectItem.controls) ? effectItem.controls : {}
  for (const group of EFFECTS_AGENT_CONTROL_GROUPS) {
    if (!isPlainObject(controls[group])) continue
    const invalid = Object.keys(controls[group]).filter((key) => !validParams.has(key))
    if (invalid.length) {
      return {
        ok: false,
        reason: 'unsupported_effect_params',
        math,
        params: invalid,
      }
    }
  }

  return { ok: true }
}

const validateEffectsAgentItems = (effectItems = []) => {
  for (const effectItem of effectItems) {
    const validation = validateEffectsAgentItem(effectItem)
    if (!validation.ok) return validation
  }
  return { ok: true }
}

const resolveEffectsAgentRangeSeconds = ({ payload, strip, fps }) => {
  const writeMode = `${payload?.writeMode || ''}`.trim()
  if (writeMode !== 'replace_effect_controls_in_range') return { ok: true, range: null }
  const range = payload?.range
  if (!isPlainObject(range)) {
    return { ok: false, reason: 'missing_effect_range', missing: ['range'] }
  }

  const clipFrameCount = Math.max(1, Math.round(finiteNum(strip?.length, 0) * fps))
  const maxStart = Math.max(0, clipFrameCount - 1)
  const startRaw = finiteNum(range.startFrame, null)
  const endRaw = finiteNum(range.endFrameExclusive, null)
  if (startRaw == null || endRaw == null) {
    return { ok: false, reason: 'invalid_effect_range', missing: ['range.startFrame', 'range.endFrameExclusive'] }
  }

  const startFrame = Math.max(0, Math.min(maxStart, Math.round(startRaw)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(clipFrameCount, Math.round(endRaw))
  )

  return {
    ok: true,
    range: {
      startFrame,
      endFrameExclusive,
      startSec: startFrame / fps,
      spanSec: (endFrameExclusive - startFrame) / fps,
    },
  }
}

const withEffectRangePatch = (patch, rangeSeconds = null) => {
  if (!rangeSeconds) return patch
  return {
    ...patch,
    time: rangeSeconds.startSec,
    span: rangeSeconds.spanSec,
  }
}

const removeMatchingEffectsForAgentItem = (store, { tuuid, suuid, strip, effectItem }) => {
  const ids = resolveStripEffectIds({
    strip,
    effectId: effectItem.effectId,
    effectCode: effectItem.effectCode,
    math: effectItemMath(effectItem),
  })
  ids.forEach((euuid) => {
    store.commit('removeEffect', { tuuid, suuid, euuid })
  })
  return ids.length
}

const buildEffectUpdatePatch = (effectItem) => {
  if (!isPlainObject(effectItem)) return {}
  const patch = {}
  if (typeof effectItem.math === 'string' && effectItem.math.trim()) {
    patch.math = effectItem.math.trim()
  }
  if (isPlainObject(effectItem.controls)) {
    const controls = { ...effectItem.controls }
    if (
      typeof controls.math === 'string' &&
      controls.math.trim() &&
      typeof patch.math === 'undefined'
    ) {
      patch.math = controls.math.trim()
    }
    if (typeof controls.math !== 'undefined') delete controls.math
    if (isPlainObject(controls.tweens)) {
      patch.tweens = controls.tweens
      delete controls.tweens
    }
    if (isPlainObject(controls.values)) {
      controls.auto = isPlainObject(controls.auto) ? { ...controls.auto } : {}
      Object.keys(controls.values).forEach((code) => {
        if (typeof controls.auto[code] === 'undefined') controls.auto[code] = 'none'
      })
    }
    if (Object.keys(controls).length) patch.controls = controls
  }
  return patch
}

const stripBaseType = (strip) => `${strip?.asset?.type || ''}`.split('/')[0]

const isMediaEffectTargetType = (type) => ['video', 'image', 'text'].includes(`${type || ''}`)

const effectCodeRequiresAdjustmentHost = (value) => {
  const code = normalizeEffectCode(value).toLowerCase()
  return code === 'postprocessing' || code === 'blur'
}

const presetRequiresAdjustmentHost = (preset) => {
  const group = `${preset?.group || ''}`.trim().toLowerCase()
  return group === 'postprocessing' || group === 'blur'
}

const effectItemRequiresAdjustmentHost = (effectItem) => {
  if (!isPlainObject(effectItem)) return false
  const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
  if (action === 'remove') return false
  return effectCodeRequiresAdjustmentHost(effectItem.effectCode)
}

const requestRequiresAdjustmentHost = ({
  effectItems = null,
  effectCode = null,
  preset = null,
} = {}) => {
  if (Array.isArray(effectItems) && effectItems.some((item) => effectItemRequiresAdjustmentHost(item))) {
    return true
  }
  return effectCodeRequiresAdjustmentHost(effectCode) || presetRequiresAdjustmentHost(preset)
}

const createAdjustmentHostOverStrip = (store, { suuid } = {}) => {
  if (!suuid) return null
  const tuuid = Object.keys(store.state.tracks).find(
    (id) => store.state.tracks[id]?.strips?.[suuid]
  )
  if (!tuuid) return null
  const clip = store.state.tracks[tuuid].strips[suuid]
  if (!clip) return null

  const t = Number(clip.start) || 0
  const len = Number(clip.length) || DEFAULT_DURATION

  // Keep the current UX invariant: adjustment host lands immediately behind the source clip.
  const tr = store.state.tracks[tuuid]
  const strips = Object.values(tr?.strips || {})
  const depth = (Number.isFinite(clip.depth) ? clip.depth : 0) + 1
  store.commit('_pushUndo', 'addAdjustmentLayerOverStrip')
  const blocked = strips.some((s) => s && !stripCanDrop(depth, t, len, s))
  debugEffectDrop('createAdjustmentHostOverStrip:start', {
    sourceStripId: suuid,
    trackId: tuuid,
    start: t,
    length: len,
    depth,
    blocked,
  })
  if (blocked) {
    store.commit('shiftTrackDepths', {
      uuid: tuuid,
      fromDepth: depth,
      delta: 1
    })
  }

  const asset = factory.createAdjustment()
  const stripId = Uuid()
  store.commit('addStrip', { auuid: asset.uuid, tuuid, time: t, depth, length: len, uuid: stripId })
  const createdStrip = store.state.tracks?.[tuuid]?.strips?.[stripId] || null
  if (!createdStrip) {
    debugEffectDrop('createAdjustmentHostOverStrip:missing-strip', {
      sourceStripId: suuid,
      trackId: tuuid,
      stripId,
    })
    return null
  }

  store.commit('activeTrack', tuuid)
  store.commit('activeStrip', stripId)
  store.commit('effectsView', true)
  store.commit('saved', false)
  store.commit('updateProjectLength')
  debugEffectDrop('createAdjustmentHostOverStrip:created', {
    sourceStripId: suuid,
    trackId: tuuid,
    stripId,
    depth: createdStrip.depth,
    start: createdStrip.start,
    length: createdStrip.length,
  })

  return {
    stripId,
    tuuid,
    length: len,
  }
}

const actions = {
    loadGlobalPresetCatalog: async (store, { force = false } = {}) => {
      if (store.state.globalPresetCatalogLoaded && !force) {
        return !store.state.globalPresetCatalogError
      }

      const { error, data } = await supabase
        .from(VYBY_PRESETS_TABLE)
        .select()
        .eq('enabled', true)
        .in('kind', [
          VYBY_PRESET_KIND_EFFECT,
          VYBY_PRESET_KIND_TEXT_MOTION,
          VYBY_PRESET_KIND_NODE_WORKFLOW,
          VYBY_PRESET_KIND_PERSONALITY,
          VYBY_PRESET_KIND_MOOD,
          VYBY_PRESET_KIND_CAMERA_BODY,
          VYBY_PRESET_KIND_CAMERA_LENS,
          VYBY_PRESET_KIND_CAMERA_FOCAL,
          VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
          VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
          VYBY_PRESET_KIND_CAMERA_MOTION,
          VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
        ])
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })

      if (error) {
        log.warn('loadGlobalPresetCatalog fallback to static presets', error)
        store.commit('setGlobalPresetCatalogError', error.message || error)
        return false
      }

      const rows = normalizeVybyPresetRows(data || [])
      const effectPresets = mergeGlobalPresetLists(
        GLOBAL_EFFECT_PRESETS,
        vybyPresetRowsToEffectPresets(rows)
      )
      const textMotionPresets = mergeGlobalPresetLists(
        GLOBAL_TEXT_MOTION_PRESETS,
        vybyPresetRowsToTextMotionPresets(rows)
      )
      const nodeWorkflowPresets = vybyPresetRowsToNodeWorkflowPresets(rows)
      const personalityPresets = vybyPresetRowsToPersonalityElements(rows)
      const moodPresets = vybyPresetRowsToMoodElements(rows)
      const cameraCatalog = buildCameraPresetCatalog(rows)

      store.commit('setGlobalPresetCatalog', {
        rows,
        effectPresets,
        textMotionPresets,
        nodeWorkflowPresets,
        personalityPresets,
        moodPresets,
        cameraCatalog,
      })
      return true
    },
    // Writes only the settings column. A moodboard edit is a small, isolated
    // change, so it should not drag the whole project (tracks, files, blobs)
    // through saveProject.
    saveMoodboard: async (
      store,
      {
        moodboard,
        cameraProfile = undefined,
        cameraProfilePresetId = undefined,
      } = {},
    ) => {
      const projectId = `${store.state.uuid || ''}`.trim()
      const userId = `${store.rootState.user?.uid || ''}`.trim()
      if (!projectId) return { ok: false, reason: 'no-project' }
      if (!userId) return { ok: false, reason: 'missing-user' }

      // A whole-project autosave replaces the complete settings object. Flush
      // any pending/in-flight save first so an older snapshot cannot finish
      // after this narrow Moodboard write and erase its camera default.
      await store.dispatch('flushAutosave')

      const normalized = normalizeMoodboard(moodboard)
      const normalizedCameraProfile = normalizeCameraProfile(
        typeof cameraProfile === 'undefined'
          ? store.state.settings?.cameraProfile
          : cameraProfile,
      )
      const normalizedCameraProfilePresetId =
        normalizeCameraProfilePresetId(
          typeof cameraProfilePresetId === 'undefined'
            ? store.state.settings?.cameraProfilePresetId
            : cameraProfilePresetId,
        )
      const nextSettings = {
        ...(jclone(store.state.settings) || {}),
        moodboard: normalized,
        cameraProfile: normalizedCameraProfile,
        cameraProfilePresetId: normalizedCameraProfilePresetId,
      }

      const savePromise = (async () => {
        const upd = await supabase
          .from('projects')
          .update({
            settings: nextSettings,
            updatedat: new Date().toISOString(),
          })
          .eq('id', projectId)
          .eq('owner', userId)
        if (upd.error) {
          log.err('saveMoodboard failed', upd.error)
          return { ok: false, reason: 'update-project-failed' }
        }

        // Mirror into local state without flipping `saved`: the row is already
        // current, so marking the project dirty would trigger a needless autosave.
        store.commit('updateSettings', {
          moodboard: normalized,
          cameraProfile: normalizedCameraProfile,
          cameraProfilePresetId: normalizedCameraProfilePresetId,
        })
        return {
          ok: true,
          moodboard: normalized,
          cameraProfile: normalizedCameraProfile,
          cameraProfilePresetId: normalizedCameraProfilePresetId,
        }
      })()
      projectSettingsSliceSavePromise = savePromise
      try {
        return await savePromise
      } finally {
        if (projectSettingsSliceSavePromise === savePromise) {
          projectSettingsSliceSavePromise = null
        }
      }
    },
    // Goals are another small project.settings slice. Keep this write narrow so
    // editing goals never triggers a full tracks/files project save.
    saveGoals: async (store, { goals } = {}) => {
      const projectId = `${store.state.uuid || ''}`.trim()
      const userId = `${store.rootState.user?.uid || ''}`.trim()
      if (!projectId) return { ok: false, reason: 'no-project' }
      if (!userId) return { ok: false, reason: 'missing-user' }

      const normalized = normalizeProjectGoals(goals)
      const nextSettings = {
        ...(jclone(store.state.settings) || {}),
        goals: normalized,
      }

      const upd = await supabase
        .from('projects')
        .update({
          settings: nextSettings,
          updatedat: new Date().toISOString(),
        })
        .eq('id', projectId)
        .eq('owner', userId)
      if (upd.error) {
        log.err('saveGoals failed', upd.error)
        return { ok: false, reason: 'update-project-failed' }
      }

      store.commit('updateSettings', { goals: normalized })
      return { ok: true, goals: normalized }
    },
    // Used by the future response-card Add button. It still funnels through the
    // same normalized full-list save and refuses exact duplicate descriptions.
    addGoal: async (store, { description } = {}) => {
      const current = normalizeProjectGoals(store.state.settings?.goals)
      if (!`${description || ''}`.trim()) {
        return { ok: false, reason: 'empty-goal' }
      }
      if (hasDuplicateProjectGoal(current, description)) {
        return { ok: false, reason: 'duplicate-goal' }
      }
      const goal = createProjectGoal(description)
      const result = await store.dispatch('saveGoals', {
        goals: [...current, goal],
      })
      return result?.ok ? { ...result, goal } : result
    },
    // Phase 1-2 (retime): wrappers
    toggleStripRetimeEnabled: (store, { tuuid, suuid, enabled, mode = null }) => {
      store.commit('toggleStripRetimeEnabled', { tuuid, suuid, enabled, mode })
      store.commit('saved', false)
    },
    addRetimePoint: (store, { tuuid, suuid, t, speed, affects }) => {
      store.commit('addRetimePoint', { tuuid, suuid, t, speed, affects })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    addRetimePointLive: (store, { tuuid, suuid, t, speed, affects }) => {
      store.commit('addRetimePointLive', { tuuid, suuid, t, speed, affects })
    },
    updateRetimePoint: (store, { tuuid, suuid, index, t, speed, affects }) => {
      store.commit('updateRetimePoint', { tuuid, suuid, index, t, speed, affects })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    updateRetimePointLive: (store, { tuuid, suuid, index, t, speed, affects }) => {
      store.commit('updateRetimePointLive', { tuuid, suuid, index, t, speed, affects })
    },
    removeRetimePoint: (store, { tuuid, suuid, index }) => {
      store.commit('removeRetimePoint', { tuuid, suuid, index })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    removeRetimePointLive: (store, { tuuid, suuid, index }) => {
      store.commit('removeRetimePointLive', { tuuid, suuid, index })
    },
    retimeAutofitLength: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('retimeAutofitLength', { tuuid, suuid, anchor })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    retimeAutofitLengthLive: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor })
      store.commit('updateProjectLength')
    },
    setRetimeAnchor: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('setRetimeAnchor', { tuuid, suuid, anchor })
      store.commit('saved', false)
    },
    setStripRetimeColor: (store, { tuuid, suuid, color }) => {
      store.commit('setStripRetimeColor', { tuuid, suuid, color })
      store.commit('saved', false)
    },
    init: async (store, project_id) => {
      await store.dispatch('loadGlobalPresetCatalog')

      const lastProject = `${project_id || ''}`.trim()
      if (!lastProject) return false
      // log.info('init?', { init: store.state.initialized, lastProject, saved: store.state.saved, isNew: store.state.isNew, uuid: store.state.uuid })
      if (store.state.initialized) {
        if (lastProject === store.state.uuid) return true
        // log.warn('!isNew, need save?')
        factory.reset()
      }
      store.commit('initialized', false)
      // log.info('init', lastProject)
      factory.on('error', (err) => {
        factory.dbg.error('store::project::factory error', err)
        console.error(factory.dbg.error('store::project::factory error', err))
      })
      const loaded = await store.dispatch('loadProject', lastProject)
      if (!loaded) {
        console.log('project was not loaded, returning to projects')
        return false
      }
      return true
    },
    resetProject: async (store) => {
      // song: defaultSong(),
      // environment: defaultEnv(),
      // picture: defaultPicture(),
      // generated: {},
      // library: {},
      // effects: fromStorage('vyby/effects', { ...VYBY_EFFECTS }),
      factory.reset()
      store.commit('reset')
    },
    newProject: async (store, options = {}) => {
      // await store.dispatch('resetProject')
      const { camera } = factory.defineScene()
      const uuid = Uuid()
      const title = await getNextDefaultProjectTitle(store)
      // console.log('new project', uuid)
      store.commit('uuid', uuid)
      store.commit('projectTitle', title)
      toSession(`${NEW_PROJECT_PANEL_ENTRY_SESSION_KEY_PREFIX}${uuid}`, true)
      if (Number.isInteger(options?.slotIndex)) {
        store.commit('projectSetting', {
          code: 'projectSlotIndex',
          value: options.slotIndex,
        })
      }
      await store.dispatch(
        'user/updatePrivateProfile',
        { last_project: null },
        { root: true }
      )
      // Scene is now project-level settings (Phase 4). Keep only the Camera strip in the rig track.
      store.dispatch('addAssetToTrack', {
        auuid: camera.uuid,
        tuuid: tracksIndex.globalRigTrackUuid,
        time: 0,
        depth: 0,
      })
      store.commit('activeTrack', tracksIndex.trackUuid1)
      let stripCamera = null
      if (!store.state.tracks[tracksIndex.globalRigTrackUuid]) {
        log.warn(
          'No store.state.tracks[tracksIndex.globalRigTrackUuid] ',
          store.state.tracks[tracksIndex.globalRigTrackUuid]
        )
        return
      }
      Object.keys(store.state.tracks[tracksIndex.globalRigTrackUuid].strips).forEach(
        (sid) => {
          const strip = store.state.tracks[tracksIndex.globalRigTrackUuid].strips[sid]
          if (strip.asset.type === 'camera') stripCamera = sid
        }
      )
      store.commit('defineSceneCamera', { cameraStrip: stripCamera })
      store.commit('updateProjectLength')
      setTimeout(() => {
        store.commit('saved', true)
      }, 500)
      return uuid
    },
    loadProject: async (store, project_id) => {
      const { data, error } = await supabase
        .from('projects')
        .select()
        .eq('id', project_id)
        .maybeSingle()
      log.info('load project', data, error, project_id)
      if (!data || error) {
        log.warn('load project failed', data, error)
        return false
      }
      await store.dispatch('resetProject')
      const layerTrackUuid = (layer) =>
        Object.keys(data.structure.tracks).find(
          (tuuid) => data.structure.tracks[tuuid].layer === layer
        )
      const t0 = layerTrackUuid(0)
      const t1 = layerTrackUuid(1)
      const t2 = layerTrackUuid(2)
      const t3 = layerTrackUuid(3)
      const t4 = layerTrackUuid(4)
      const tg = Object.keys(data.structure.tracks).find(
        (tuuid) => data.structure.tracks[tuuid].type === 'global'
      )
      if (t0) tracksIndex.audioTrackUuid = t0
      if (t1) tracksIndex.trackUuid1 = t1
      if (t2) tracksIndex.trackUuid2 = t2
      if (t3) tracksIndex.trackUuid3 = t3
      if (t4) tracksIndex.trackUuid4 = t4
      if (tg) tracksIndex.globalRigTrackUuid = tg
      // log.log('trackIndex update', { tracksIndex, t0, t1, t2, t3, t4 })
      const getAssetStrip = (assetType) =>
        Object.keys(data.structure.tracks).reduce((acc, tuuid) => {
          if (acc) return acc
          const track = data.structure.tracks[tuuid]
          return Object.values(track.strips).reduce((acc2, strip) => {
            if (acc2) return acc2
            if (strip.asset && strip.asset.type === assetType) return strip
            return acc2
          }, null)
        }, null)
      const cameraStripObj = getAssetStrip('camera')
      if (cameraStripObj) {
        factory.defineScene(cameraStripObj.asset.uuid)
      } else {
        log.warn('Skip camera asset definition; camera strip not found in project structure')
      }

      // Ensure non-media assets exist in the runtime factory BEFORE tracks are committed.
      // (Adjustment layer assets have no corresponding file to load.)
      try {
        Object.keys(data.structure.tracks || {}).forEach((tuuid) => {
          const track = data.structure.tracks[tuuid]
          Object.values(track?.strips || {}).forEach((strip) => {
            if (!strip || !strip.asset) return
            factory.ensureFromObject(strip.asset)
          })
        })
      } catch (e) { /* noop */ }

      const savedAssetSources =
        data.settings && typeof data.settings.assetSources === 'object'
          ? data.settings.assetSources
          : {}
      const savedAssetAddedAt =
        data.settings && typeof data.settings.assetAddedAt === 'object'
          ? data.settings.assetAddedAt
          : {}
      const savedDerivedClipMeta =
        data.settings && typeof data.settings.derivedClipMeta === 'object'
          ? data.settings.derivedClipMeta
          : {}
      const projectFileIds = Array.isArray(data.files) ? data.files : []
      const projectFiles = projectFileIds.length
        ? await supabase
          .from('files')
          .select()
          .in('id', projectFileIds)
        : { data: [], error: null }
      // log.log('load project files', projectFiles, data.files)
      if (projectFiles.error) {
        log.warn('project files info error', projectFiles.error)
        return false
      }
      (projectFiles.data || []).forEach((f) => {
        if (!f?.id) return
        const source = normalizeAssetSource(savedAssetSources[f.id], 'upload')
        store.commit('libraryMediaNew', {
          uuid: f.id,
          type: f.mime || '',
          meta: {
            ...projectFileMeta(f, source, savedAssetAddedAt[f.id]),
            ...(savedDerivedClipMeta[f.id] || {}),
          },
        })
      })
      // Commit the project shell before fetching heavy S3 blobs so the editor can render quickly.
      store.commit('uuid', data.id)
      store.commit('projectTitle', data.name)
      store.commit('songUpdate', data.intro.song)
      store.commit('envUpdate', data.intro.environment)
      store.commit('pictureUpdate', data.intro.picture)
      // Deprecated settings: ignore stale saved values so the current default
      // wins. useVideoFrameEngineV2 was false in older saved projects (V2 is
      // now the only video path); useWebCodecsPreview/useUnifiedTrackBuffer
      // were removed entirely.
      const ignoredSavedSettings = new Set([
        'useVideoFrameEngineV2',
        'useWebCodecsPreview',
        'useUnifiedTrackBuffer',
      ])
      Object.keys(data.settings).forEach((code) => {
        if (ignoredSavedSettings.has(code)) return
        if (code === 'nodeGraph') {
          store.commit('setNodeGraph', data.settings[code])
        } else {
          store.commit('projectSetting', { code, value: data.settings[code] })
        }
      })
      store.commit('redefineTracks', data.structure.tracks)
      if (store.state.tracks?.[tracksIndex.trackUuid1]) {
        store.commit('activeTrack', tracksIndex.trackUuid1)
      }
      store.commit('activeStrip', null)
      store.commit('activeKeyframe', null)
      store.commit('activeKeyframeSelection', null)
      store.commit('activeSmartKey', null)
      store.commit('defineSceneCamera', { cameraStrip: cameraStripObj ? cameraStripObj.uuid : null })
      store.commit('updateProjectLength')
      // console.log('loaded!')
      setTimeout(() => {
        store.commit('saved', true)
      }, 500)
      store.dispatch('hydrateProjectAssets', {
        projectId: data.id,
        files: sortProjectFilesForHydration(projectFiles.data || [], data.structure.tracks),
        savedAssetSources,
        savedAssetAddedAt,
        savedDerivedClipMeta,
      })
      void trackAnalyticsEvent(ANALYTICS_EVENTS.PROJECT_OPENED, {
        projectId: data.id,
        entityType: 'project',
        entityId: data.id,
      })
      return true
    },
    hydrateProjectAssets: async (store, {
      projectId,
      files = [],
      savedAssetSources = {},
      savedAssetAddedAt = {},
      savedDerivedClipMeta = {},
    } = {}) => {
      const rows = Array.isArray(files) ? files.filter((f) => f?.id && f?.path) : []
      // Debug helper: dump per-asset hydration/readiness state in the console.
      if (typeof window !== 'undefined') {
        window.__vybyHydrationState = () => ({
          hydration: jclone(store.state.assetHydration || {}),
          assets: Object.keys(store.state.library || {}).map((uuid) => {
            const asset = factory.asset(uuid)
            return {
              uuid,
              type: asset?.type || store.state.library[uuid]?.type || null,
              inFactory: !!asset,
              ready: !!asset?._asset?.ready,
              width: asset?.meta?.width ?? null,
              height: asset?.meta?.height ?? null,
              hasThumb: !!asset?.thumbnail,
              blobSize: asset?.blob?.size ?? null,
              blobSource: asset?.meta?.blobSource || null,
              libraryReady: !!store.state.library[uuid]?.ready,
            }
          }),
        })
      }
      store.commit('assetHydrationStart', {
        projectId,
        total: rows.length,
      })
      if (!rows.length) {
        store.commit('assetHydrationComplete')
        return true
      }

      let loaded = 0
      let failed = null
      for (let i = 0; i < rows.length; i++) {
        if (projectId && store.state.uuid !== projectId) return false
        const f = rows[i]
        try {
          // Proxy video Phase 3: when a seek-dense proxy exists, it becomes
          // the working blob for every consumer (decode, audio, export,
          // transcription). The original stays stored untouched.
          const isVideo = `${f.mime || ''}`.startsWith('video')
          let blobSource = isVideo && f.proxy_path ? 'proxy' : 'original'
          let fblob = null
          try {
            fblob = await blobFromUrl(
              `${VUE_APP_S3}/${blobSource === 'proxy' ? f.proxy_path : f.path}`
            )
          } catch (err) {
            if (blobSource !== 'proxy') throw err
            log.warn('[proxy] proxy blob fetch failed; falling back to original', f.id, err)
            blobSource = 'original'
            fblob = await blobFromUrl(`${VUE_APP_S3}/${f.path}`)
          }
          if (blobSource === 'proxy') {
            log.info('[proxy] hydrating from proxy blob', { assetUuid: f.id, proxyPath: f.proxy_path })
          }
          // Keep the ORIGINAL filename; the proxy is always mp4, so prefer the
          // fetched content type for it over the original row mime.
          const fname = f.path.split('/').pop()
          const fmime = blobSource === 'proxy' ? (fblob.type || 'video/mp4') : f.mime
          const ffile = new File([fblob], fname, { type: fmime })
          ffile.uuid = f.id
          const source = normalizeAssetSource(savedAssetSources[f.id], 'upload')
          await store.dispatch('addMedia', {
            file: ffile,
            source,
            markDirty: false,
            meta: {
              ...projectFileMeta(f, source, savedAssetAddedAt[f.id]),
              ...(savedDerivedClipMeta[f.id] || {}),
              blobSource,
            },
          })
          // Block until the asset is fully prepared (video dimensions,
          // thumbnail, etc.) so the preview player only mounts — and the
          // first editor.tracks() only attaches clips — against ready assets.
          const readiness = await waitForAssetReady(factory.asset(f.id))
          if (!readiness.ready) {
            log.warn(
              'asset not ready after hydrate',
              f.id,
              readiness.reason,
              readiness.err || ''
            )
          }
        } catch (err) {
          failed = err
          log.warn('project asset hydrate failed', f?.id, err)
        } finally {
          loaded += 1
          if (!projectId || store.state.uuid === projectId) {
            store.commit('assetHydrationProgress', {
              loaded,
              total: rows.length,
            })
          }
        }
      }

      if (projectId && store.state.uuid !== projectId) return false
      if (failed) {
        store.commit('assetHydrationError', failed)
        return false
      }
      store.commit('assetHydrationComplete')
      store.commit('saved', true)
      return true
    },
    // Proxy video Phase 2 (Proxy_Video.md): called by the V2 video engine when
    // an asset's demux completes, with the sample store's keyframe stats.
    // Decides whether the asset needs a seek-dense proxy and requests one.
    videoProxyStats: async (store, { assetUuid, stats } = {}) => {
      if (!assetUuid || !stats) return false
      const existing = store.state.videoProxy[assetUuid]
      // Re-parses (strip detach/re-attach re-registers the decode session)
      // must not restart an active or finished flow.
      if (existing && ['requesting', 'ready'].includes(existing.status)) {
        return false
      }
      const syncCount = Number(stats.syncCount) || 0
      const durationSec = Number(stats.durationSec) || 0
      const meta = store.state.library[assetUuid]?.meta || {}
      if (meta.proxyPath) {
        store.commit('videoProxyState', {
          uuid: assetUuid,
          updates: { status: 'ready', proxyPath: meta.proxyPath, syncCount, durationSec },
        })
        return false
      }
      const proxyWorthy =
        syncCount <= 1 ||
        (durationSec > PROXY_MIN_DURATION_SEC &&
          durationSec / Math.max(1, syncCount) > PROXY_SPARSE_GOP_SEC)
      if (!proxyWorthy) {
        store.commit('videoProxyState', {
          uuid: assetUuid,
          updates: { status: 'not-needed', syncCount, durationSec },
        })
        return false
      }
      log.info('[proxy] sparse-keyframe video detected', {
        assetUuid,
        syncCount,
        durationSec: Number(durationSec.toFixed(2)),
      })
      if (!meta.persisted) {
        // No files row yet — the backend can't locate the original. The save
        // flow re-kicks this once the upload lands.
        log.info('[proxy] asset not saved yet; proxy will be requested after project save', assetUuid)
        store.commit('videoProxyState', {
          uuid: assetUuid,
          updates: { status: 'awaiting-persist', syncCount, durationSec },
        })
        return false
      }
      store.commit('videoProxyState', {
        uuid: assetUuid,
        updates: { syncCount, durationSec },
      })
      return store.dispatch('requestVideoProxy', { assetUuid })
    },
    // Ask the backend to transcode a seek-dense proxy for a persisted video
    // asset ("media:proxy" route). Progress arrives via the socket bind
    // "media:proxy:progress" -> videoProxyProgress.
    requestVideoProxy: async (store, { assetUuid } = {}) => {
      if (!assetUuid) return { success: false, error: 'NO_ASSET' }
      const current = store.state.videoProxy[assetUuid]
      if (current && ['requesting', 'ready'].includes(current.status)) {
        return { success: false, error: 'ALREADY_' + current.status.toUpperCase() }
      }
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('[proxy] core io not connected', assetUuid)
        return { success: false, error: 'NO_SOCKET' }
      }
      store.commit('videoProxyState', {
        uuid: assetUuid,
        updates: { status: 'requesting', ratio: 0, error: null },
      })
      log.info('[proxy] requesting transcode', assetUuid)
      let reply = null
      try {
        // Long-form transcodes take minutes; the timeout is a backstop.
        reply = await socket.timeout(1800000).emitWithAck('media:proxy', { fileId: assetUuid })
      } catch (err) {
        log.err('[proxy] socket error', assetUuid, err)
        store.commit('videoProxyState', {
          uuid: assetUuid,
          updates: { status: 'error', error: 'SOCKET_TIMEOUT' },
        })
        return { success: false, error: 'SOCKET_TIMEOUT' }
      }
      if (!reply?.success || !reply.proxyPath) {
        log.err('[proxy] transcode failed', assetUuid, reply)
        store.commit('videoProxyState', {
          uuid: assetUuid,
          updates: { status: 'error', error: reply?.error || 'PROXY_FAILED' },
        })
        return { success: false, error: reply?.error || 'PROXY_FAILED' }
      }
      store.commit('videoProxyState', {
        uuid: assetUuid,
        updates: { status: 'ready', ratio: 1, proxyPath: reply.proxyPath },
      })
      // Stamp the in-session asset meta; hydration reads proxy_path from the
      // files row on the next load (the backend already persisted it).
      store.commit('libraryMediaMeta', {
        uuid: assetUuid,
        meta: { proxyPath: reply.proxyPath },
      })
      log.info('[proxy] ready', {
        assetUuid,
        proxyPath: reply.proxyPath,
        cached: !!reply.cached,
      })
      // Phase 3: hot-swap the working blob in the live session. Non-fatal —
      // if it fails, the proxy still applies on the next project load.
      try {
        await store.dispatch('hotSwapVideoProxy', {
          assetUuid,
          proxyPath: reply.proxyPath,
        })
      } catch (err) {
        log.warn('[proxy] hot swap failed; proxy applies on next project load', assetUuid, err)
      }
      return { success: true, proxyPath: reply.proxyPath, cached: !!reply.cached }
    },
    // Proxy video Phase 3: replace a live asset's working blob with the
    // seek-dense proxy and rebuild its strips so every consumer (V2 decode
    // session, decoded audio, textures) re-reads the new blob.
    hotSwapVideoProxy: async (store, { assetUuid, proxyPath } = {}) => {
      if (!assetUuid || !proxyPath) return { success: false, error: 'BAD_ARGS' }
      const asset = factory.asset(assetUuid)
      if (!asset || asset.type !== 'video') {
        return { success: false, error: 'ASSET_NOT_LIVE' }
      }
      // blobFromUrl returns a raw ArrayBuffer (axios responseType), so wrap it
      // in a File like the hydrate path does — downstream consumers
      // (URL.createObjectURL, VideoSampleStore.parse's blob.arrayBuffer, the
      // audio MP4WCPlayer) all require a Blob. Keep the original filename;
      // the proxy is always mp4.
      const fbuf = await blobFromUrl(`${VUE_APP_S3}/${proxyPath}`)
      const fname = asset._asset?.meta?.name
        || asset.meta?.name
        || proxyPath.split('/').pop()
        || `${assetUuid}.mp4`
      const fblob = new File([fbuf], fname, { type: 'video/mp4' })
      fblob.uuid = assetUuid
      asset._asset.blob = fblob
      asset._asset.mime = fblob.type
      asset.meta.blobSource = 'proxy'
      store.commit('libraryMediaMeta', {
        uuid: assetUuid,
        meta: { blobSource: 'proxy' },
      })
      // Invalidate the shared decode cache entry so new sessions load the
      // proxy blob instead of the original.
      try {
        assetDecodeManager.registerAsset({
          uuid: assetUuid,
          blob: fblob,
          meta: asset.meta ? { ...asset.meta } : {},
        })
      } catch (e) { /* decode manager guard */ }
      const editor = store.state.editorInstance
        || (typeof window !== 'undefined' ? window.__vybyEditor : null)
      let rebuilt = 0
      if (editor && typeof editor.rebuildAssetStrips === 'function') {
        rebuilt = editor.rebuildAssetStrips(assetUuid)
      }
      log.info('[proxy] hot swap applied', { assetUuid, proxyPath, stripsRebuilt: rebuilt })
      return { success: true, rebuilt }
    },
    // Socket bind target for "media:proxy:progress" (see store/index.js login).
    videoProxyProgress: async (store, { data } = {}) => {
      const fileId = data?.fileId
      if (!fileId) return
      const ratio = Number.isFinite(data.ratio) ? data.ratio : null
      const prev = store.state.videoProxy[fileId]?.ratio || 0
      store.commit('videoProxyState', { uuid: fileId, updates: { ratio } })
      // Log at ~10% steps to keep the console readable.
      if (ratio === null || Math.floor(ratio * 10) > Math.floor(prev * 10)) {
        log.info(`[proxy] transcoding ${ratio === null ? '…' : Math.round(ratio * 100) + '%'}`, fileId)
      }
    },
    initProject: async (store, info) => {
      store.dispatch('addMedia', info.song.file)
      store.commit('projectTitle', `${info.song.title} by ${info.song.artist}`)
      store.commit('projectSetting', { code: 'bpm', value: info.song.bpm })
      // store.commit('songUpdate', info.song)
      // store.commit('envUpdate', info.environment)
      // store.commit('pictureUpdate', info.picture)
      store.dispatch('ai/calibrate', info, { root: true })
      store.dispatch('ai/genInit', null, { root: true })
    },
    scheduleAutosave: async (store, { delayMs = AUTOSAVE_DELAY_MS } = {}) => {
      if (autosavePromise) return false
      if (autosaveTimer) return true
      if (!canAutosaveProject(store.state)) {
        if (store.state.saved) {
          store.commit('autosaveStatus', { status: 'idle', error: null })
        }
        return false
      }
      store.commit('autosaveStatus', { status: 'pending', error: null })
      autosaveTimer = setTimeout(() => {
        autosaveTimer = null
        store.dispatch('runAutosave')
      }, Math.max(0, Number(delayMs) || AUTOSAVE_DELAY_MS))
      return true
    },
    cancelAutosave: async (store) => {
      clearAutosaveTimer()
      if (store.state.autosave?.status === 'pending') {
        store.commit('autosaveStatus', { status: 'idle', error: null })
      }
    },
    runAutosave: async (store) => {
      clearAutosaveTimer()
      if (autosavePromise) return autosavePromise
      if (!canAutosaveProject(store.state)) {
        if (store.state.saved) {
          store.commit('autosaveStatus', { status: 'idle', error: null })
        }
        return !!store.state.saved
      }

      const projectId = store.state.uuid
      store.commit('autosaveStatus', { status: 'saving', error: null })
      autosavePromise = (async () => {
        try {
          const ok = await store.dispatch('saveProject', { source: 'autosave' })
          if (store.state.uuid !== projectId) return !!store.state.saved
          if (ok && store.state.saved) {
            store.commit('autosaveStatus', {
              status: 'saved',
              error: null,
              lastSavedAt: Date.now(),
            })
            return true
          }
          store.commit('autosaveStatus', {
            status: 'error',
            error: 'Autosave failed',
          })
          return false
        } catch (err) {
          store.commit('autosaveStatus', {
            status: 'error',
            error: autosaveErrorMessage(err),
          })
          return false
        } finally {
          autosavePromise = null
        }
      })()
      return autosavePromise
    },
    flushAutosave: async (store) => {
      if (autosavePromise) return autosavePromise
      if (!autosaveTimer) return !!store.state.saved
      clearAutosaveTimer()
      if (!canAutosaveProject(store.state)) return !!store.state.saved
      return store.dispatch('runAutosave')
    },
    saveProject: async (store) => {
      if (projectSettingsSliceSavePromise) {
        await projectSettingsSliceSavePromise
      }
      if (store.state.saved) return true

      if (!store.state.uuid) {
        console.log('store.state.uuid!!! SKIP', store.state.uuid)
        return false
      }
      // get all project files from files table to update list of files in project table
      const all_proj_files = await supabase
        .from('files')
        .select()
        .eq('project', store.state.uuid)
      //.maybeSingle()
      console.log('all_proj_files', all_proj_files.data)

      const all_proj_files_ids = all_proj_files.data
        ? Object.values(all_proj_files.data).map((item) => item.id)
        : []

      console.log('all_proj_files_ids', all_proj_files_ids)
      var existing = await supabase
        .from('projects')
        .select()
        .eq('id', store.state.uuid)
        .maybeSingle()
      if (existing.error) {
        console.warn('presave load existing error', existing.error)
      }
      const isNew = !existing.data // || !existing.data.files.length
      console.log('isNew existing', isNew, existing)

      if (existing.error) {
        console.warn('presave load existing error', existing.error)
      }
      const store_library_file_ids = Object.keys(store.state.library)
      // console.log('proj_files', store_library_file_ids, all_proj_files_ids)

      const files = store_library_file_ids

      if (isNew) {
        existing.data = { files: [] }
      }
      if (files.length) {
        existing.data.files = all_proj_files_ids
      }
      console.warn(files)

      const uploads = files.filter((fid) =>
        shouldUploadLibraryAsset(store.state.library[fid], all_proj_files_ids)
      )
      console.log('Not exists uploads::', uploads)
      // Upload the bytes first, and only upsert the files row once the upload
      // landed. The files row is what hydration loads from on the next project
      // open — a row without bytes (or bytes without a row) leaves the clip
      // permanently unloadable (turquoise strip). Failures keep the asset
      // unpersisted so the next save retries it.
      const uploadOutcomes = await Promise.all(
        uploads.map(async (fid) => {
          const asset = factory.asset(fid)
          if (!asset) {
            console.warn('Skip asset file ', fid)
            return null
          }
          const fname = asset.meta.name
          const ext = fname.split('.').pop()
          const row = {
            id: fid,
            owner: store.rootState.user.uid,
            project: store.state.uuid,
            path: `${store.rootState.user.uid}/${store.state.uuid}/${fid}.${ext}`,
            mime: asset.blob.type,
            size: asset.blob.size,
            onwall: true,
          }
          try {
            const uploadedPath = await store.dispatch(
              'user/uploadFile',
              { file: asset.blob, name: `${store.state.uuid}/${fid}.${ext}` },
              { root: true }
            )
            if (!uploadedPath) {
              log.err('asset upload failed', fid, row.path)
              return { fid, row, error: 'UPLOAD_FAILED' }
            }
          } catch (err) {
            log.err('asset upload error', fid, err)
            return { fid, row, error: err }
          }
          const upserted = await supabase.from('files').upsert(row).select()
          if (upserted.error) {
            log.err('files row upsert failed', fid, upserted.error)
            return { fid, row, error: upserted.error }
          }
          console.log('upsert updatedFile', row)
          return { fid, row, error: null }
        })
      )
      const uploadFileRecords = uploadOutcomes
        .filter((outcome) => outcome && !outcome.error)
        .map((outcome) => outcome.row)
      const failedUploads = uploadOutcomes.filter(
        (outcome) => outcome && outcome.error
      )
      const projectFileRows = [
        ...((all_proj_files && all_proj_files.data) || []),
        ...uploadFileRecords,
      ]
      const projectSettings = { ...store.state.settings }
      projectSettings.assetSources = assetSourceMapFromLibrary(
        store.state.library,
        files
      )
      projectSettings.assetAddedAt = assetAddedAtMapFromLibrary(
        store.state.library,
        files
      )
      projectSettings.derivedClipMeta = derivedClipMetaMapFromLibrary(
        store.state.library,
        files
      )
      const currentBanner = normalizeProjectBanner(projectSettings.projectBanner)
      projectSettings.projectBanner = currentBanner?.explicit
        ? currentBanner
        : pickDefaultProjectBanner(files, projectFileRows)
      const project = {
        name: store.state.settings.title,
        owner: store.rootState.user.uid,
        published: isNew ? false : existing.data.published,
        createdat: isNew ? new Date() : existing.data.createdat,
        updatedat: new Date(),
        intro: {
          song: {
            artist: store.state.song.artist,
            title: store.state.song.title,
            bpm: store.state.song.bpm,
            fileid: null,
          },
          environment: store.state.environment,
          picture: store.state.picture,
        },
        settings: projectSettings,
        structure: {
          tracks: store.state.tracks,
        },
        files,
      }
      uploadFileRecords.forEach((row) => {
        store.commit('libraryMediaMeta', {
          uuid: row.id,
          meta: {
            persisted: true,
            path: row.path,
          },
        })
        // Proxy video Phase 2: a sparse-keyframe verdict recorded before the
        // first save can be acted on now that the files row exists.
        if (store.state.videoProxy[row.id]?.status === 'awaiting-persist') {
          store.dispatch('requestVideoProxy', { assetUuid: row.id })
        }
      })
      let saved = null
      if (isNew) {
        console.warn('Insert NEW proj files', project)
        project.id = store.state.uuid
        saved = await supabase.from('projects').upsert(project)
      } else {
        //console.log('store.state.uuid', store.state.uuid)
        saved = await supabase
          .from('projects')
          .update(project)
          .eq('id', store.state.uuid)
        console.warn('Update proj files', project)
      }
      if (!saved.error) {
        if (isNew) {
          void trackAnalyticsEvent(ANALYTICS_EVENTS.PROJECT_CREATED, {
            projectId: store.state.uuid,
            entityType: 'project',
            entityId: store.state.uuid,
          })
        }
        await store.dispatch(
          'user/updatePrivateProfile',
          { last_project: store.state.uuid },
          { root: true }
        )
      }
      // Media persistence failures must not be masked by a successful project
      // row save: keep the project dirty so autosave retries the uploads, and
      // surface the error where the autosave UI reads it.
      if (failedUploads.length) {
        log.err(
          'save incomplete: media failed to persist',
          failedUploads.map((outcome) => outcome.fid)
        )
        store.commit('autosaveStatus', {
          status: 'error',
          error: `${failedUploads.length} media file(s) failed to save; will retry`,
        })
      }
      const ok = !saved.error && !failedUploads.length
      store.commit('saved', ok)
      store.commit('redirect', true)
      console.log('SAVE PROJECT!', saved.error || 'no errors', project)
      return ok
    },
    upsertPreset: (store, preset) => {
      const uuid = (preset || {}).uuid || Uuid()
      store.commit('upsertEffectPreset', { uuid, ...preset })
      return uuid
    },
    deletePreset: (store, preset) => {
      store.commit('delEffectPreset', preset || {})
    },
    addGlobalFileToProject: async (store, fileRow = {}) => {
      const fileId = `${fileRow?.id || ''}`.trim()
      const userId = `${store.rootState.user?.uid || ''}`.trim()
      if (!fileId) return { ok: false, reason: 'missing-asset' }
      if (!userId) return { ok: false, reason: 'missing-user' }

      const activeProjectId = `${store.state.uuid || ''}`.trim()
      const lastProjectId = `${store.rootState.user?.settings?.last_project || ''}`.trim()
      const targetProjectId = activeProjectId || lastProjectId
      if (!targetProjectId) return { ok: false, reason: 'missing-project' }

      const { data: sourceFile, error: sourceError } = await supabase
        .from('files')
        .select()
        .eq('id', fileId)
        .eq('owner', userId)
        .maybeSingle()
      if (sourceError || !sourceFile?.path) {
        console.error('load global file before project link failed', sourceError)
        return { ok: false, reason: 'load-file-failed' }
      }

      const projectInfo = await supabase
        .from('projects')
        .select('id, files, settings')
        .eq('id', targetProjectId)
        .eq('owner', userId)
        .maybeSingle()
      if (projectInfo.error || !projectInfo.data) {
        console.error('load project before global file link failed', projectInfo.error)
        return { ok: false, reason: 'load-project-failed' }
      }

      const projectFiles = Array.isArray(projectInfo.data.files)
        ? projectInfo.data.files
        : []
      const alreadyInProject = projectFiles.includes(fileId)
      const activeProject = activeProjectId === targetProjectId
      const prevSettings = activeProject
        ? { ...(projectInfo.data.settings || {}), ...(store.state.settings || {}) }
        : (projectInfo.data.settings || {})
      const prevAssetSources =
        prevSettings.assetSources && typeof prevSettings.assetSources === 'object'
          ? prevSettings.assetSources
          : {}
      const prevAssetAddedAt =
        prevSettings.assetAddedAt && typeof prevSettings.assetAddedAt === 'object'
          ? prevSettings.assetAddedAt
          : {}
      const source = alreadyInProject
        ? normalizeAssetSource(prevAssetSources[fileId], 'upload')
        : 'global'
      const addedAt = prevAssetAddedAt[fileId] || new Date().toISOString()
      const assetSources = {
        ...prevAssetSources,
        [fileId]: source,
      }
      const assetAddedAt = {
        ...prevAssetAddedAt,
        [fileId]: addedAt,
      }
      const nextSettings = {
        ...prevSettings,
        assetSources,
        assetAddedAt,
      }
      const nextFiles = alreadyInProject
        ? projectFiles
        : [...projectFiles, fileId]

      let linkedFile = null
      let linkedBlobSource = 'original'
      if (activeProject && !store.state.library?.[fileId]) {
        try {
          // Proxy video Phase 3: prefer the seek-dense proxy as working blob.
          const isVideo = `${sourceFile.mime || ''}`.startsWith('video')
          linkedBlobSource = isVideo && sourceFile.proxy_path ? 'proxy' : 'original'
          let fblob = null
          try {
            fblob = await blobFromUrl(
              `${VUE_APP_S3}/${linkedBlobSource === 'proxy' ? sourceFile.proxy_path : sourceFile.path}`
            )
          } catch (err) {
            if (linkedBlobSource !== 'proxy') throw err
            log.warn('[proxy] proxy blob fetch failed; falling back to original', fileId, err)
            linkedBlobSource = 'original'
            fblob = await blobFromUrl(`${VUE_APP_S3}/${sourceFile.path}`)
          }
          const fname = sourceFile.path.split('/').pop() || `${fileId}`
          const fmime = linkedBlobSource === 'proxy'
            ? (fblob.type || 'video/mp4')
            : (sourceFile.mime || fblob.type || '')
          linkedFile = new File([fblob], fname, { type: fmime })
          linkedFile.uuid = fileId
        } catch (err) {
          console.error('hydrate global file before project link failed', err)
          return { ok: false, reason: 'hydrate-file-failed' }
        }
      }

      const upd = await supabase
        .from('projects')
        .update({
          files: [...new Set(nextFiles)],
          settings: nextSettings,
          updatedat: new Date().toISOString(),
        })
        .eq('id', targetProjectId)
        .eq('owner', userId)
      if (upd.error) {
        console.error('link global file to project failed', upd.error)
        return { ok: false, reason: 'update-project-failed' }
      }

      if (activeProject) {
        const wasSaved = !!store.state.saved
        store.commit('projectSetting', { code: 'assetSources', value: assetSources })
        store.commit('projectSetting', { code: 'assetAddedAt', value: assetAddedAt })
        if (linkedFile) {
          await store.dispatch('addMedia', {
            file: linkedFile,
            source,
            markDirty: false,
            meta: {
              ...projectFileMeta(sourceFile, source, addedAt),
              blobSource: linkedBlobSource,
            },
          })
        } else if (store.state.library?.[fileId]) {
          store.commit('libraryMediaMeta', {
            uuid: fileId,
            meta: projectFileMeta(sourceFile, source, addedAt),
          })
        }
        if (wasSaved) store.commit('saved', true)
      }

      return {
        ok: true,
        already: alreadyInProject,
        projectId: targetProjectId,
        assetId: fileId,
      }
    },
    addS3Media: async (store, { file, out }) => {
      console.log('adds3media', file, out)
      file.uuid = out.fid
      const source = 'generation'
      const filePath = filePathFromGeneratedOut(out)
      await store.dispatch('addMedia', {
        file,
        source,
        meta: {
          persisted: true,
          path: filePath,
        },
      })
      const projectInfo = await supabase
        .from('projects')
        .select('files, settings')
        .eq('id', store.state.uuid)
        .maybeSingle()
      if (!projectInfo.data || projectInfo.error) {
        console.warn('project not loaded, files not saved!', projectInfo.error)
        return
      }
      const prevSettings = projectInfo.data.settings || store.state.settings || {}
      const assetSources = {
        ...(prevSettings.assetSources || {}),
        [out.fid]: source,
      }
      store.commit('projectSetting', {
        code: 'assetSources',
        value: {
          ...((store.state.settings && store.state.settings.assetSources) || {}),
          [out.fid]: source,
        },
      })
      const projectFiles = Array.isArray(projectInfo.data.files)
        ? projectInfo.data.files
        : []
      const nextFiles = projectFiles.includes(out.fid)
        ? projectFiles
        : [...projectFiles, out.fid]
      await supabase
        .from('projects')
        .update({
          files: [...new Set(nextFiles)],
          settings: {
            ...prevSettings,
            assetSources,
          },
        })
        .eq('id', store.state.uuid)
    },
    addMedia: async (store, payload) => {
      //addMedia: async (store, { file, uploadNew }) => {
      //store.dispatch('addMediaToLibrary', file)
      const { file, uploadNew, source, meta, markDirty } = normalizeMediaPayload(payload)
      if (!file) return
      const asset = factory.fromFile(file, file.uuid || null)
      //console.log('addMedia', { file, asset })
      if (!asset) return null
      const uuid = asset.uuid
      const libraryType = file.type || asset._asset?.mime || (asset.type === 'd3' ? 'model/gltf-binary' : '')
      const baseMeta = {
        ...meta,
        source,
      }
      asset.meta.source = source
      // Proxy video: mark whether this asset's working blob is the original
      // media or a seek-dense proxy (surfaced in V2 decode debug state).
      asset.meta.blobSource = baseMeta.blobSource || 'original'
      store.commit('libraryMediaNew', { uuid, type: libraryType, meta: baseMeta })
      asset.on('asset.error', (err) => {
        console.error('loading asset error', err)
      })
      asset.on('ready', () => {
        asset.meta.source = source
        store.commit('libraryMediaData', {
          uuid,
          data: {
            ...asset.meta,
            ...baseMeta,
            source,
          },
        })
        if (markDirty) store.commit('saved', false)
        console.info('save added files with project')
        if (uploadNew) store.dispatch('saveProject')
        else if (markDirty) log.warn('uploadNew not set or false. Skipping saveProject')
        try {
          const editor = store.state.editorInstance || null
          if (editor && typeof editor.tracks === 'function') {
            editor.tracks(jclone(store.state.tracks))
          }
          // Any video strip attached before this asset became ready (deferred
          // placeholder or broken pre-ready geometry) needs a full dispose +
          // re-attach; updateTracks() alone never repairs existing objects.
          if (
            asset.type === 'video' &&
            editor &&
            typeof editor.rebuildAssetStrips === 'function'
          ) {
            editor.rebuildAssetStrips(uuid)
          }
        } catch (e) { /* editor refresh guard */ }
        try {
          assetDecodeManager.registerAsset({
            uuid,
            meta: asset.meta ? { ...asset.meta } : {},
            options: {
              decodeAudio: asset.type === 'audio' || asset.type === 'video',
              decodeVideo: asset.type === 'video',
            }
          })
        } catch (e) { console.warn('asset decode register meta failed', e) }
      })
      try {
        assetDecodeManager.registerAsset({
          uuid,
          blob: asset.blob || null,
          meta: asset.meta ? { ...asset.meta } : {},
          options: {
            decodeAudio: asset.type === 'audio' || asset.type === 'video',
            decodeVideo: asset.type === 'video',
          }
        })
      } catch (e) { console.warn('asset decode register failed', e) }
      return uuid
    },
    deleteLibraryAsset: async (store, payload) => {
      const uuid =
        payload && typeof payload === 'object' ? payload.uuid : payload
      const save =
        !payload || typeof payload !== 'object' || payload.save !== false
      const auuid = `${uuid || ''}`.trim()
      if (!auuid) return { ok: false, reason: 'missing-asset' }

      const assetInfo = store.state.library?.[auuid]
      if (!assetInfo) return { ok: false, reason: 'missing-asset' }

      if (projectUsesAsset(store.state.tracks, auuid)) {
        return { ok: false, reason: 'used-on-timeline' }
      }

      try {
        assetDecodeManager.dispose(auuid, { force: true })
      } catch (e) { /* noop */ }
      try {
        factory.removeAsset?.(auuid)
      } catch (e) { /* noop */ }

      store.commit('libraryMediaRemove', auuid)
      store.commit('projectSetting', {
        code: 'assetSources',
        value: assetSourceMapFromLibrary(
          store.state.library,
          Object.keys(store.state.library || {})
        ),
      })
      store.commit('projectSetting', {
        code: 'assetAddedAt',
        value: assetAddedAtMapFromLibrary(
          store.state.library,
          Object.keys(store.state.library || {})
        ),
      })
      store.commit('projectSetting', {
        code: 'derivedClipMeta',
        value: derivedClipMetaMapFromLibrary(
          store.state.library,
          Object.keys(store.state.library || {})
        ),
      })

      if (save && store.state.uuid) {
        await store.dispatch('saveProject')
      }

      return { ok: true }
    },
    addText: async (store, { text, thumbnail }) => {
      if (!text) return
      const asset = await factory.fromText(text, thumbnail, {
        settings: store.state.settings,
        zeroFrame: store.state.timeline?.zeroFrame3D,
      })
      if (!asset) return null
      const uuid = asset.uuid
      store.commit('libraryMediaNew', { uuid, type: 'text/plain' })
      asset.on('ready', () => {
        store.commit('libraryMediaData', { uuid, data: asset.meta })
        store.commit('saved', false)
      })
      return uuid
    },
    editText: async (store, { auuid, text, thumbnail }) => {
      const asset = factory.asset(auuid)
      if (!asset) return
      asset.loadText(text, thumbnail)
      asset.on('ready', () => {
        store.commit('libraryMediaData', { uuid: auuid, data: asset.meta })
        store.commit('saved', false)
      })
    },
    autodrop: (store) => {
      const dest = store.getters.autodropTrack
      // get last time at track
      const track = store.state.tracks[dest]
      if (!track) {
        console.error('autodrop track not found!', { dest })
        return false
      }
      const len = store.state.dnd.meta.duration || DEFAULT_DURATION
      const strips = Object.values(track.strips)
      const canNotHere =
        strips.length &&
        strips.some((strip) => {
          // if (src.uuid === strip.uuid) return false
          return !stripCanDrop(
            track.depth,
            store.state.timeline.currentTime,
            len,
            strip
          )
        })
      /*
       */
      const lastTime = canNotHere
        ? Object.values(track.strips).reduce((acc, strip) => {
            return Math.max(acc, strip.start + strip.length)
          }, 0)
        : store.state.timeline.currentTime
      store.dispatch('addAssetToTrack', {
        auuid: store.state.dnd.uuid,
        tuuid: dest,
        time: lastTime,
      })
      store.commit('dnd', null)
    },
    addAssetToTrack: (store, { auuid, depth, tuuid, time }) => {
      // console.log('ADD ASSET TO TRACK', auuid, tuuid, time)
      const toAudio = tuuid === tracksIndex.audioTrackUuid
      const audioInit =
        toAudio &&
        !Object.keys(store.state.tracks[tracksIndex.audioTrackUuid].strips)
          .length
      // snapshot BEFORE change
      store.commit('_pushUndo', 'addAssetToTrack')
      store.commit('addStrip', { auuid, depth, tuuid, time })
      if (audioInit && store.state.settings.autoAdoptBPM)
        store.commit('updateProjectBPM', auuid)
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 3: add asset with lane insertion/shift ops (ultra-expand modal)
    addAssetToTrackWithLaneOp: (store, { auuid, depth, tuuid, time, laneOp = null }) => {
      const toAudio = tuuid === tracksIndex.audioTrackUuid
      const audioInit =
        toAudio &&
        !Object.keys(store.state.tracks[tracksIndex.audioTrackUuid].strips)
          .length

      // snapshot BEFORE change (single undo entry for lane shift + add)
      store.commit('_pushUndo', 'addAssetToTrackWithLaneOp')

      if (laneOp && typeof laneOp === 'object') {
        if (laneOp.type === 'insert' && Number.isFinite(Number(laneOp.atDepth))) {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: Number(laneOp.atDepth), delta: 1 })
        } else if (laneOp.type === 'bottom') {
          // create a new bottom lane by shifting everything up by 1
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: 0, delta: 1 })
        }
      }

      store.commit('addStrip', { auuid, depth, tuuid, time })

      if (audioInit && store.state.settings.autoAdoptBPM)
        store.commit('updateProjectBPM', auuid)
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    moveStrip: (store, { suuid, tuuid, depth, tfuuid, time }) => {
      // snapshot BEFORE change
      store.commit('_pushUndo', 'moveStrip')
      store.commit('moveStrip', { suuid, tuuid, depth, tfuuid, time })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    moveSelectedStrips: (store, { moves = [], anchorUuid = null } = {}) => {
      const input = Array.isArray(moves) ? moves : []
      const movingIds = new Set(input.map((move) => move?.suuid).filter(Boolean))
      if (!input.length || movingIds.size !== input.length) return
      const list = input.map((move) => {
        if (!move?.suuid || !move?.tuuid || !move?.tfuuid) return null
        const sourceTrack = store.state.tracks?.[move.tfuuid]
        const targetTrack = store.state.tracks?.[move.tuuid]
        const strip = sourceTrack?.strips?.[move.suuid]
        const time = Number(move.time)
        const depth = Number(move.depth)
        if (!sourceTrack || !targetTrack || !strip) return null
        if (!Number.isFinite(time) || !Number.isFinite(depth)) return null
        if (time < 0 || depth < 0) return null
        const duration = Math.max(1e-6, Number(strip.length) || DEFAULT_DURATION)
        const collides = Object.values(targetTrack.strips || {}).some((other) => {
          if (!other || movingIds.has(other.uuid)) return false
          return !stripCanDrop(depth, time, duration, other)
        })
        if (collides) return null
        return {
          suuid: move.suuid,
          tuuid: move.tuuid,
          tfuuid: move.tfuuid,
          depth,
          time,
          strip,
        }
      })
      if (list.length !== input.length || list.some((move) => !move)) return
      const hasMovement = list.some((move) => {
        const currentStart = Number(move.strip.start) || 0
        const currentDepth = Math.max(0, Number(move.strip.depth) || 0)
        return move.tuuid !== move.tfuuid ||
          Math.abs(move.time - currentStart) > 1e-6 ||
          Math.abs(move.depth - currentDepth) > 1e-6
      })
      if (!hasMovement) return
      store.commit('_pushUndo', 'moveSelectedStrips')
      list.forEach((move) => {
        store.commit('moveStrip', {
          suuid: move.suuid,
          tuuid: move.tuuid,
          tfuuid: move.tfuuid,
          depth: move.depth,
          time: move.time,
        })
        store.commit('ensureTrackMaxDepth', { uuid: move.tuuid, depth: move.depth })
      })
      const selected = list.map((move) => ({ tuuid: move.tuuid, suuid: move.suuid }))
      const anchor = anchorUuid && selected.find((item) => item.suuid === anchorUuid)
      const ordered = anchor
        ? [...selected.filter((item) => item.suuid !== anchorUuid), anchor]
        : selected
      store.commit('setSelectedStrips', ordered)
      if (anchorUuid) store.dispatch('activeStrip', anchorUuid)
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 3: move strip with lane insertion/shift ops (ultra-expand modal)
    moveStripWithLaneOp: (store, { suuid, tuuid, depth, tfuuid, time, laneOp = null }) => {
      // snapshot BEFORE change (single undo entry for lane shift + move)
      store.commit('_pushUndo', 'moveStripWithLaneOp')

      if (laneOp && typeof laneOp === 'object') {
        if (laneOp.type === 'insert' && Number.isFinite(Number(laneOp.atDepth))) {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: Number(laneOp.atDepth), delta: 1 })
        } else if (laneOp.type === 'bottom') {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: 0, delta: 1 })
        }
      }

      store.commit('moveStrip', { suuid, tuuid, depth, tfuuid, time })
      store.commit('ensureTrackMaxDepth', { uuid: tuuid, depth })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Move an existing adjustment strip to immediate lane behind a target clip.
    // If the desired lane is occupied for that span, shift deeper lanes to keep "immediate behind" deterministic.
    moveAdjustmentLayerBehindStrip: (store, { suuid, targetStripId } = {}) => {
      if (!suuid || !targetStripId || suuid === targetStripId) return null
      const sourceLoc = findStripLocation(store.state, suuid)
      const targetLoc = findStripLocation(store.state, targetStripId)
      if (!sourceLoc || !targetLoc) return null
      const { tuuid: sourceTrackId, strip: sourceStrip } = sourceLoc
      const { tuuid: targetTrackId, strip: targetStrip } = targetLoc
      const sourceType = `${sourceStrip?.asset?.type || ''}`.split('/')[0]
      const targetType = `${targetStrip?.asset?.type || ''}`.split('/')[0]
      if (sourceType !== 'adjustment') return null
      if (!['video', 'image', 'text'].includes(targetType)) return null

      const start = Number(targetStrip.start) || 0
      const length = Number(targetStrip.length) || DEFAULT_DURATION
      const desiredDepth = Math.max(0, (Number.isFinite(Number(targetStrip.depth)) ? Number(targetStrip.depth) : 0) + 1)
      const targetTrack = store.state.tracks?.[targetTrackId]
      if (!targetTrack) return null

      store.commit('_pushUndo', 'moveAdjustmentLayerBehindStrip')
      const strips = Object.values(targetTrack.strips || {})
      const blocked = strips.some((s) => {
        if (!s || s.uuid === suuid) return false
        return !stripCanDrop(desiredDepth, start, length, s)
      })
      if (blocked) {
        store.commit('shiftTrackDepths', {
          uuid: targetTrackId,
          fromDepth: desiredDepth,
          delta: 1
        })
      }

      store.commit('moveStrip', {
        suuid,
        tuuid: targetTrackId,
        tfuuid: sourceTrackId,
        depth: desiredDepth,
        time: start,
      })
      // Use resize mutation directly to keep this flow as one undoable operation.
      store.commit('resizeActiveStrip', {
        start,
        length,
      })
      store.commit('ensureTrackMaxDepth', {
        uuid: targetTrackId,
        depth: desiredDepth
      })
      store.commit('saved', false)
      store.commit('updateProjectLength')
      return suuid
    },
    removeStrip: (store, { track, strip, force = false } = {}) => {
      if (!track || !strip) return
      // Camera strip is required (auto-managed). Block deletion unless explicitly forced.
      try {
        const cam = store.state.cameraStrip
        const isCamera = cam && strip === cam
        if (isCamera && !force) return
        const s = store.state.tracks?.[track]?.strips?.[strip]
        if (s?.asset?.type === 'camera' && !force) return
      } catch (e) { /* noop */ }
      // snapshot BEFORE change
      store.commit('_pushUndo', 'removeStrip')
      store.commit('removeStrip', { track, strip, force })
      store.commit('saved', false)
      store.commit('activeStrip', null)
    },
    removeSelectedStrips: (store) => {
      const selected = store.getters.selectedStrips || []
      const deletable = selected.filter(({ strip }) => {
        if (!strip) return false
        const cameraStrip = store.state.cameraStrip
        if (cameraStrip && strip.uuid === cameraStrip) return false
        if (strip.asset?.type === 'camera') return false
        return true
      })
      if (!deletable.length) return
      store.commit('_pushUndo', 'removeSelectedStrips')
      deletable.forEach(({ tuuid, suuid }) => {
        store.commit('removeStrip', { track: tuuid, strip: suuid })
      })
      store.commit('clearSelectedStrips')
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    playerSelect: (store, selection) => {
      if (!selection) {
        store.commit('activeStrip', null)
        // store.commit('activeTrack', null)
      } else {
        store.commit('activeTrack', selection.track)
        store.commit('activeStrip', selection.strip)
      }
    },
    // ------   REFACTORED   ---------------
    // active elements selection/deselection
    activeTrack: (store, uuid) => {
      if (store.state.timeline.activeTrack === uuid) return
      store.commit('activeTrack', uuid)
      store.commit('activeStrip', null)
      store.commit('activeKeyframe', null)
      store.commit('tool', [])
    },
    activeStrip: (store, uuid) => {
      const trackUuid = Object.keys(store.state.tracks).find((track) =>
        Object.keys(store.state.tracks[track].strips).includes(uuid)
      )
      if (!trackUuid) return
      if (store.state.timeline.activeStrip === uuid) {
        const selected = store.state.timeline.selectedStrips || []
        const alreadySelected = selected.some((item) => item?.tuuid === trackUuid && item?.suuid === uuid)
        if (alreadySelected) return
      }
      if (store.state.timeline.activeTrack !== trackUuid)
        store.commit('activeTrack', trackUuid)
      store.commit('activeStrip', uuid)
      store.commit('activeKeyframe', null)
      const strip = store.state.tracks?.[trackUuid]?.strips?.[uuid] || null
      store.commit('tool', strip?.asset?.type === 'd3' ? ['position'] : [])
    },
    activeKeyframe: (store, uuid) => {
      if (store.state.timeline.activeKeyframe === uuid) return
      store.commit('activeKeyframe', uuid)
      store.commit('tool', [])
      if (uuid) {
        const strip = store.getters.activeStrip
        const kf = store.getters.activeKeyframe
        if (strip) store.commit('currentTime', strip.start + kf.time)
      }
    },
    selectSmartKey: (store, { tuuid, suuid, prop, part }) => {
      const track = store.state.tracks[tuuid]
      if (!track) return
      const strip = track.strips[suuid]
      if (!strip) return
      // Phase 4: virtual transform lane (transform2d) uses canonical per-axis group for timing.
      const sk = strip.smartKeys || {}
      const timingProp = isTransform2dEnvelopeProp(prop)
        ? getTransform2dCanonicalProp()
        : isQuickbarZoomEnvelopeProp(prop)
          ? getQuickbarZoomCanonicalProp(prop)
          : prop
      const group = sk[timingProp] || { offsets: { entry: 0, exit: 0 } }
      const clamp = (v) => Math.max(0, Math.min(strip.length, v))
      const tStart = 0
      const tEnd = strip.length
      const tLeft = clamp(group.offsets?.entry || 0)
      const tRight = clamp(strip.length - (group.offsets?.exit || 0))
      const tRel = part === 'start' ? tStart : part === 'left' ? tLeft : part === 'right' ? tRight : tEnd
      const timeAbs = strip.start + tRel
      const curr = store.state.timeline.activeSmartKey
      // Toggle off if selecting the same smart key
      if (curr && curr.strip === suuid && curr.track === tuuid && curr.prop === prop && curr.part === part) {
        store.commit('activeSmartKey', null)
        return
      }
      // Ensure active selection points to this strip
      store.commit('activeTrack', tuuid)
      store.commit('activeStrip', suuid)
      // Set active smart key and move playhead
      store.commit('activeSmartKey', { track: tuuid, strip: suuid, prop, part, timeAbs })
      store.commit('currentTime', timeAbs)
    },
    activeKeyframeProps: (store, update) => {
      // console.log('akp', update)
      store.commit('activeKeyframeProps', update)
    },
    tool: (store, tool) => {
      const ntool = store.state.tool === tool ? [] : tool
      store.commit('tool', ntool)
    },
    toggleTool: (store, tool) => {
      if (store.state.tool.includes(tool)) store.commit('tool', [])
      else store.commit('tool', [tool])
    },
    resizeActiveStrip: (store, { start, length }) => {
      store.commit('resizeActiveStrip', { start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Regular trim action (no proportional keyframe scaling)
    trimActiveStrip: (store, { start, length }) => {
      store.commit('trimActiveStrip', { start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Speed trim action; computes speed via mapping
    speedTrimActiveStrip: (store, { anchor = 'back', start, length }) => {
      store.commit('speedTrimActiveStrip', { anchor, start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    splitActiveStripAtPlayhead: (store) => {
      store.commit('splitActiveStripAtPlayhead')
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Transcript warp Phase 1: word-level transcription of an audio strip via
    // the backend (ElevenLabs Scribe). Word times are in source/media seconds
    // (relative to the audio file start), independent of strip trims/retimes.
    // Auto Clipping Phase 1: video strips are also accepted — their audio is
    // extracted client-side (mono 16kHz WAV) and sent through the same route,
    // so word times are video source seconds too.
    transcribeStrip: async (store, { tuuid, suuid } = {}) => {
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) {
        log.err('transcribeStrip: strip not found', { tuuid, suuid })
        return { success: false, error: 'STRIP_NOT_FOUND' }
      }
      const asset = factory.asset(strip.asset?.uuid)
      if (!asset || (asset.type !== 'audio' && asset.type !== 'video')) {
        log.err('transcribeStrip: not an audio/video strip', { suuid })
        return { success: false, error: 'NOT_AUDIO' }
      }
      if (!asset.blob) {
        log.err('transcribeStrip: media blob not loaded', { suuid })
        return { success: false, error: 'NO_AUDIO_BLOB' }
      }
      let file = asset.blob
      let mime = asset.blob.type || 'audio/mpeg'
      let name = asset.meta?.name || 'audio'
      if (asset.type === 'video') {
        try {
          const extracted = await extractTranscriptionAudio({
            // Phase A: DecodedAudioStore is keyed by asset uuid (shared across
            // strips of one source); strip uuid kept as legacy fallback.
            sourceKey: asset.uuid,
            stripUuid: suuid,
            blob: asset.blob,
            name,
          })
          file = extracted.blob
          mime = extracted.mime
          name = extracted.filename
          log.info('transcribeStrip: video audio extracted', {
            suuid,
            source: extracted.source,
            durationSec: Number(extracted.durationSeconds.toFixed(2)),
            bytes: extracted.blob.size,
          })
        } catch (err) {
          log.err('transcribeStrip: video audio extraction failed', err)
          return { success: false, error: 'AUDIO_EXTRACT_FAILED' }
        }
      }

      let reply = null
      if (!AI_BACKEND_BASE) {
        return { success: false, error: 'BACKEND_NOT_CONFIGURED' }
      }
      const jwt = `${store.rootState.user?.jwt || ''}`.trim()
      if (!jwt) return { success: false, error: 'AUTH_REQUIRED' }

      const body = new FormData()
      body.append('mime', mime)
      body.append('name', name)
      body.append('file', file, name)
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), TRANSCRIBE_HTTP_TIMEOUT_MS)
      try {
        const response = await fetch(`${AI_BACKEND_BASE}/vbapi/audio/transcribe`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${jwt}` },
          body,
          signal: controller.signal,
        })
        try {
          reply = await response.json()
        } catch {
          const authFailed = response.status === 401 || response.status === 418
          return {
            success: false,
            error: authFailed ? 'AUTH_REQUIRED' : 'TRANSCRIBE_HTTP_ERROR',
            message: authFailed ? null : `Transcription request failed (${response.status})`,
          }
        }
        if (!response.ok) {
          return {
            success: false,
            error: reply?.error || 'TRANSCRIBE_HTTP_ERROR',
            message: reply?.message || null,
          }
        }
      } catch (err) {
        const timedOut = controller.signal.aborted
        log.err('transcribeStrip: HTTP error', err)
        return {
          success: false,
          error: timedOut ? 'TRANSCRIBE_TIMEOUT' : 'TRANSCRIBE_NETWORK_ERROR',
        }
      } finally {
        clearTimeout(timeout)
      }
      if (!reply?.success || !reply.transcript) {
        log.err('transcribeStrip: backend error', reply)
        return {
          success: false,
          error: reply?.error || 'TRANSCRIBE_FAILED',
          message: reply?.message || null,
        }
      }

      const transcript = {
        version: 1,
        assetUuid: asset.uuid,
        createdAt: Date.now(),
        ...reply.transcript,
      }
      store.commit('setStripProp', {
        tuuid,
        suuid,
        code: 'transcript',
        value: transcript,
      })
      store.commit('saved', false)

      // Phase 1 test helper: inspect word timing in the console.
      console.groupCollapsed(
        `[transcript] ${transcript.words.length} words ` +
          `(${transcript.language || '??'}) — strip ${suuid}`
      )
      console.log(transcript.text)
      console.table(
        transcript.words.map((w) => ({
          word: w.text,
          start: w.start,
          end: w.end,
          chars: w.chars ? w.chars.length : 0,
        }))
      )
      console.groupEnd()

      return { success: true, transcript }
    },
    // Voice Overs Phase 2: list provider voices for the picker (cached per
    // provider for the session; `force` refetches).
    listVoiceoverVoices: async (store, { provider = 'elevenlabs', force = false } = {}) => {
      if (!force && voiceoverVoicesCache[provider]) {
        return { success: true, voices: voiceoverVoicesCache[provider], cached: true }
      }
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('listVoiceoverVoices: core io not connected')
        return { success: false, error: 'NO_SOCKET' }
      }
      let reply = null
      try {
        reply = await socket.timeout(30000).emitWithAck('voices:list', { provider })
      } catch (err) {
        log.err('listVoiceoverVoices: socket error', err)
        return { success: false, error: 'SOCKET_TIMEOUT' }
      }
      if (!reply?.success || !Array.isArray(reply.voices)) {
        log.err('listVoiceoverVoices: backend error', reply)
        return {
          success: false,
          error: reply?.error || 'VOICES_LIST_FAILED',
          message: reply?.message || null,
        }
      }
      voiceoverVoicesCache[provider] = reply.voices
      return { success: true, voices: reply.voices }
    },
    // Voice Overs Phase 2: run a voice-over provider on an audio/video strip
    // and auto-insert the result as a new audio strip aligned with the source.
    // ElevenLabs Voice Changer preserves the source timing exactly, so the new
    // strip copies the source strip's timeline position and source window.
    voiceoverStrip: async (
      store,
      { tuuid, suuid, provider = 'elevenlabs', voiceId = null, voiceName = null } = {}
    ) => {
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) {
        log.err('voiceoverStrip: strip not found', { tuuid, suuid })
        return { success: false, error: 'STRIP_NOT_FOUND' }
      }
      const asset = factory.asset(strip.asset?.uuid)
      if (!asset || (asset.type !== 'audio' && asset.type !== 'video')) {
        log.err('voiceoverStrip: not an audio/video strip', { suuid })
        return { success: false, error: 'NOT_AUDIO' }
      }
      if (!asset.blob) {
        log.err('voiceoverStrip: media blob not loaded', { suuid })
        return { success: false, error: 'NO_AUDIO_BLOB' }
      }
      if (!voiceId) return { success: false, error: 'NO_VOICE' }
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('voiceoverStrip: core io not connected')
        return { success: false, error: 'NO_SOCKET' }
      }

      let file = asset.blob
      let mime = asset.blob.type || 'audio/mpeg'
      let name = asset.meta?.name || 'audio'
      let durationSec = finitePositive(asset.meta?.duration, null)
      if (asset.type === 'video') {
        try {
          const extracted = await extractTranscriptionAudio({
            sourceKey: asset.uuid,
            stripUuid: suuid,
            blob: asset.blob,
            name,
          })
          file = extracted.blob
          mime = extracted.mime
          name = extracted.filename
          durationSec = finitePositive(extracted.durationSeconds, durationSec)
        } catch (err) {
          log.err('voiceoverStrip: video audio extraction failed', err)
          return { success: false, error: 'AUDIO_EXTRACT_FAILED' }
        }
      }
      // ElevenLabs Voice Changer caps input around 5 minutes; fail fast with a
      // clear error instead of a slow provider rejection.
      if (durationSec && durationSec > VOICEOVER_MAX_DURATION_SEC) {
        return {
          success: false,
          error: 'AUDIO_TOO_LONG',
          message: `Audio is ${Math.round(durationSec)}s; voice overs support up to ${VOICEOVER_MAX_DURATION_SEC}s`,
        }
      }

      let reply = null
      try {
        reply = await socket.timeout(300000).emitWithAck('audio:voiceover', {
          provider,
          file,
          mime,
          name,
          voiceId,
          project: store.state.uuid,
        })
      } catch (err) {
        log.err('voiceoverStrip: socket error', err)
        return { success: false, error: 'SOCKET_TIMEOUT' }
      }
      if (!reply?.success || !reply.output?.fid || !reply.output?.filePath) {
        log.err('voiceoverStrip: backend error', reply)
        return {
          success: false,
          error: reply?.error || 'VOICEOVER_FAILED',
          message: reply?.message || null,
        }
      }
      const out = reply.output

      // Import the generated audio into the library (same path as other
      // generated media), then wait until the asset knows its duration.
      let blob = null
      try {
        const base = `${VUE_APP_S3 || ''}`.trim().replace(/\/+$/, '')
        const url = `${base}/${`${out.filePath}`.replace(/^\/+/, '')}`
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`fetch failed (${resp.status})`)
        blob = await resp.blob()
      } catch (err) {
        log.err('voiceoverStrip: generated audio fetch failed', err)
        return { success: false, error: 'OUTPUT_FETCH_FAILED' }
      }
      const genFile = new File([blob], out.name || `voiceover-${out.fid}.mp3`, {
        type: out.mime || 'audio/mpeg',
      })
      await store.dispatch('addS3Media', { file: genFile, out })
      const newAsset = factory.asset(out.fid)
      const readiness = await waitForAssetReady(newAsset)
      if (!readiness.ready) {
        log.err('voiceoverStrip: generated asset not ready', readiness)
        return { success: false, error: 'OUTPUT_ASSET_NOT_READY' }
      }

      // One undo step: insert the new audio strip aligned with the source
      // strip on the lowest free audio lane. Copying sourceStart keeps
      // trimmed/split source strips aligned (the converted audio covers the
      // whole source media, same as the transcription pipeline).
      store.commit('_pushUndo', 'voiceover')
      const audioTrack = tracksIndex.audioTrackUuid
      const time = Math.max(0, finiteNum(strip.start, 0))
      const length = Math.max(
        0.05,
        finitePositive(strip.length, null) ||
          finitePositive(newAsset.meta?.duration, DEFAULT_DURATION)
      )
      const depth = pickInsertDepth({
        state: store.state,
        tuuid: audioTrack,
        time,
        length,
      })
      store.commit('addStrip', {
        auuid: out.fid,
        tuuid: audioTrack,
        time,
        depth,
        length,
      })
      const newStripUuid = store.state.timeline.activeStrip
      if (newStripUuid) {
        const sourceStart = Math.max(0, finiteNum(strip.sourceStart, 0))
        if (sourceStart > 0) {
          store.commit('setStripProp', {
            tuuid: audioTrack,
            suuid: newStripUuid,
            code: 'sourceStart',
            value: sourceStart,
          })
        }
        store.commit('setStripProp', {
          tuuid: audioTrack,
          suuid: newStripUuid,
          code: 'voiceoverSource',
          value: {
            stripUuid: suuid,
            assetUuid: strip.asset?.uuid || null,
            provider,
            voiceId,
            voiceName: voiceName || null,
          },
        })
        store.commit('activeTrack', audioTrack)
        store.commit('activeStrip', newStripUuid)
      }
      store.commit('saved', false)
      store.commit('updateProjectLength')

      log.info('voiceoverStrip: done', {
        provider,
        voiceId,
        fid: out.fid,
        stripUuid: newStripUuid,
      })
      return { success: true, fid: out.fid, stripUuid: newStripUuid }
    },
    // Auto Clipping Phase 2: send the strip's transcript to the Clip Finder
    // agent and get back clip ranges in source seconds. Pure selection — no
    // strips are created here (that's Phase 3). tuuid/suuid default to the
    // active strip so this can be driven from the console:
    //   $store.dispatch('project/clipFinderSelectClips', { userGuidance: '...' })
    clipFinderSelectClips: async (
      store,
      { tuuid = null, suuid = null, userGuidance = '', targets = null } = {}
    ) => {
      if (!tuuid || !suuid) {
        suuid = store.state.timeline.activeStrip
        tuuid = store.state.timeline.activeTrack
      }
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) {
        log.err('clipFinderSelectClips: strip not found', { tuuid, suuid })
        return { success: false, error: 'STRIP_NOT_FOUND' }
      }
      const words = strip.transcript?.words
      if (!Array.isArray(words) || !words.length) {
        log.err('clipFinderSelectClips: strip has no transcript', { suuid })
        return { success: false, error: 'NO_TRANSCRIPT' }
      }
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('clipFinderSelectClips: core io not connected')
        return { success: false, error: 'NO_SOCKET' }
      }

      // Sentence-scale phrases (looser gap, higher word cap than captions):
      // fewer, longer segments keep the agent prompt compact on long videos.
      const CLIP_PHRASE_GAP_SEC = 0.6
      const CLIP_PHRASE_MAX_WORDS = 40
      const phrases = segmentTranscript(words, {
        gapSec: CLIP_PHRASE_GAP_SEC,
        maxWords: CLIP_PHRASE_MAX_WORDS,
      }).map((p, i) => ({ i, start: p.start, end: p.end, text: p.text }))
      if (!phrases.length) {
        return { success: false, error: 'NO_PHRASES' }
      }

      const asset = factory.asset(strip.asset?.uuid)
      const durationSeconds = Number.isFinite(Number(asset?.meta?.duration))
        ? Number(asset.meta.duration)
        : phrases[phrases.length - 1].end

      let reply = null
      try {
        reply = await socket.timeout(180000).emitWithAck('clip_finder:select_clips', {
          userGuidance,
          targets,
          video: {
            durationSeconds,
            language: strip.transcript?.language || null,
          },
          phrases,
        })
      } catch (err) {
        log.err('clipFinderSelectClips: socket error', err)
        return { success: false, error: socketAckErrorCode(err) }
      }
      if (!reply?.success || !reply.selection) {
        log.err('clipFinderSelectClips: backend error', reply)
        return {
          success: false,
          error: reply?.error || 'CLIP_SELECTION_FAILED',
          message: reply?.message || null,
          raw: reply?.raw || null,
        }
      }

      const selection = reply.selection
      // Phase 2 test helper: inspect the agent's picks in the console.
      if (selection.schema === 'clip_finder.refuse.v1') {
        console.warn(`[clip-finder] refused: ${selection.reason}`)
      } else {
        console.groupCollapsed(
          `[clip-finder] ${selection.clips.length} clips from ${phrases.length} phrases — strip ${suuid}`
        )
        console.table(
          selection.clips.map((c) => ({
            start: Number(c.start.toFixed(2)),
            end: Number(c.end.toFixed(2)),
            seconds: Number((c.end - c.start).toFixed(2)),
            title: c.title,
            reason: c.reason,
          }))
        )
        if (selection.notes) console.log('notes:', selection.notes)
        console.groupEnd()
      }

      return { success: true, selection, phrases, targets: reply.targets || null }
    },
    // Auto Clipping Phase 3: full pipeline for a video strip — transcribe if
    // needed, ask the Clip Finder agent for clip ranges, then create one video
    // strip per clip. Clip strips reference the SAME video asset with their
    // sourceStart/sourceDuration window set to the clip range (no media
    // processing), are laid out sequentially after the source strip, tagged
    // with `autoClipSource`, and carry a copy of the transcript so per-clip
    // captions work later. One undo step for the whole run.
    // `dryRun: true` only logs the planned strips.
    // Phase 5: `withCaptions` also generates timed caption strips for each
    // created clip (optionally animated via `captionPresetId`), all inside the
    // same single undo step.
    autoClipStrip: async (
      store,
      {
        tuuid = null,
        suuid = null,
        userGuidance = '',
        targets = null,
        dryRun = false,
        withCaptions = false,
        captionPresetId = null,
      } = {}
    ) => {
      if (!tuuid || !suuid) {
        suuid = store.state.timeline.activeStrip
        tuuid = store.state.timeline.activeTrack
      }
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) {
        log.err('autoClipStrip: strip not found', { tuuid, suuid })
        return { success: false, error: 'STRIP_NOT_FOUND' }
      }
      const asset = factory.asset(strip.asset?.uuid)
      if (!asset || asset.type !== 'video') {
        log.err('autoClipStrip: not a video strip', { suuid })
        return { success: false, error: 'NOT_VIDEO' }
      }

      if (!strip.transcript?.words?.length) {
        const res = await store.dispatch('transcribeStrip', { tuuid, suuid })
        if (!res?.success) return res
      }

      const picked = await store.dispatch('clipFinderSelectClips', {
        tuuid,
        suuid,
        userGuidance,
        targets,
      })
      if (!picked?.success) return picked
      const selection = picked.selection
      if (selection.schema === 'clip_finder.refuse.v1') {
        return { success: false, error: 'AGENT_REFUSED', message: selection.reason }
      }
      if (!selection.clips.length) {
        return { success: false, error: 'NO_CLIPS', message: selection.notes || null }
      }

      // Breathing room around the spoken range so cuts don't feel abrupt.
      // Pads are clamped so adjacent clips never overlap in source time.
      const PAD_IN_SEC = 0.15
      const PAD_OUT_SEC = 0.3
      const TIMELINE_GAP_SEC = 0.5
      const mediaDuration = Number.isFinite(Number(asset.meta?.duration))
        ? Number(asset.meta.duration)
        : Math.max(...selection.clips.map((c) => c.end))
      const ordered = [...selection.clips].sort((a, b) => a.start - b.start)
      const windows = ordered.map((clip, i) => {
        const prevEnd = i > 0 ? ordered[i - 1].end : 0
        const nextStart = i < ordered.length - 1 ? ordered[i + 1].start : mediaDuration
        const sourceStart = Math.max(prevEnd, clip.start - PAD_IN_SEC, 0)
        const sourceEnd = Math.min(nextStart, clip.end + PAD_OUT_SEC, mediaDuration)
        return {
          ...clip,
          sourceStart,
          sourceDuration: Math.max(0.1, sourceEnd - sourceStart),
        }
      })

      // Lay clips out back-to-back after the source strip.
      const sourceStripEnd = (Number(strip.start) || 0) + (Number(strip.length) || 0)
      let cursor = sourceStripEnd + TIMELINE_GAP_SEC

      const planned = windows.map((w) => {
        const plan = {
          title: w.title,
          time: cursor,
          length: w.sourceDuration,
          sourceStart: w.sourceStart,
          reason: w.reason,
        }
        cursor += w.sourceDuration + TIMELINE_GAP_SEC
        return plan
      })

      // Phase 3 test helper: inspect placement before/without creating strips.
      console.groupCollapsed(
        `[auto-clip] ${planned.length} clip strips${dryRun ? ' — dry run' : ''} — strip ${suuid}`
      )
      console.table(
        planned.map((p) => ({
          title: p.title,
          timelineStart: Number(p.time.toFixed(2)),
          seconds: Number(p.length.toFixed(2)),
          sourceIn: Number(p.sourceStart.toFixed(2)),
          sourceOut: Number((p.sourceStart + p.length).toFixed(2)),
        }))
      )
      if (selection.notes) console.log('notes:', selection.notes)
      console.groupEnd()

      if (dryRun) return { success: true, planned, selection, dryRun: true }

      store.commit('_pushUndo', 'autoClip')

      const createdStrips = []
      let lastStripUuid = null
      for (const plan of planned) {
        const depth = pickInsertDepth({
          state: store.state,
          tuuid,
          time: plan.time,
          length: plan.length,
        })
        store.commit('addStrip', {
          auuid: asset.uuid,
          tuuid,
          time: plan.time,
          depth,
          length: plan.length,
        })
        const newStripUuid = store.state.timeline.activeStrip
        if (!newStripUuid || newStripUuid === lastStripUuid) continue
        store.commit('setStripProp', {
          tuuid,
          suuid: newStripUuid,
          code: 'sourceStart',
          value: plan.sourceStart,
        })
        store.commit('setStripProp', {
          tuuid,
          suuid: newStripUuid,
          code: 'autoClipSource',
          value: {
            stripUuid: suuid,
            assetUuid: asset.uuid,
            title: plan.title,
            reason: plan.reason,
            range: { start: plan.sourceStart, end: plan.sourceStart + plan.length },
          },
        })
        // Copy the transcript so per-clip caption generation works: the clip
        // strip's own sourceStart/timeScale map the shared source-second word
        // times into its window.
        store.commit('setStripProp', {
          tuuid,
          suuid: newStripUuid,
          code: 'transcript',
          value: jclone(strip.transcript),
        })
        lastStripUuid = newStripUuid
        createdStrips.push(newStripUuid)
      }

      // Phase 5: per-clip caption strips. Each clip strip carries the copied
      // transcript, and generateCaptionStrips keeps only the phrases inside
      // that clip's source window. skipUndo keeps everything one undo step.
      let captionedClips = 0
      if (withCaptions) {
        for (const clipStripUuid of createdStrips) {
          const res = await store.dispatch('generateCaptionStrips', {
            tuuid,
            suuid: clipStripUuid,
            presetId: captionPresetId,
            skipUndo: true,
          })
          if (res?.success) captionedClips++
          else log.err('autoClipStrip: caption generation failed for clip', { clipStripUuid, res })
        }
      }

      if (lastStripUuid) {
        store.commit('activeTrack', tuuid)
        store.commit('activeStrip', lastStripUuid)
        store.commit('saved', false)
        store.commit('updateProjectLength')
      }

      return {
        success: true,
        created: createdStrips.length,
        createdStrips,
        captionedClips: withCaptions ? captionedClips : null,
        planned,
        selection,
      }
    },
    // Captions Phase 3: generate timed text strips from an audio strip's
    // transcript. Phrases split by inter-word gap (strip.captionSensitivity,
    // loose↔tight) plus a soft word cap. Strips are created through the same
    // addText/addStrip path as manual text entry, so SmartKeys/text-animation
    // behavior is identical to hand-made text strips. `dryRun` only logs the
    // segmentation (Phase 1 test helper). Phase 5a: optional `presetId`
    // applies a text motion preset to each generated strip (null = no motion).
    // Auto Clipping Phase 5: works for video strips too (anything carrying a
    // transcript). `skipUndo` lets a caller that already pushed an undo
    // snapshot (autoClipStrip) keep the whole operation one undo step.
    generateCaptionStrips: async (store, { tuuid, suuid, presetId = null, dryRun = false, skipUndo = false } = {}) => {
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) {
        log.err('generateCaptionStrips: strip not found', { tuuid, suuid })
        return { success: false, error: 'STRIP_NOT_FOUND' }
      }
      const words = strip.transcript?.words
      if (!Array.isArray(words) || !words.length) {
        return { success: false, error: 'NO_TRANSCRIPT' }
      }

      const rawSensitivity = Number(strip.captionSensitivity)
      const sensitivity = Number.isFinite(rawSensitivity)
        ? Math.max(0, Math.min(1, rawSensitivity))
        : DEFAULT_CAPTION_SENSITIVITY
      const gapSec = sensitivityToGapSec(sensitivity)
      const phrases = segmentTranscript(words, { gapSec })

      // Phase 5a+: landing-point timing. With a motion preset, the entry
      // animation plays BEFORE the spoken phrase so the 'left' anchor (the
      // landing) hits the word's timestamp exactly — the strip starts early
      // by the preset's entry offset, and the exit plays in a tail after the
      // phrase ends. Preset offsets are in seconds.
      const motionPresets = store.state.globalTextMotionPresets || GLOBAL_TEXT_MOTION_PRESETS
      const motionPreset = presetId
        ? normalizeTextMotionPreset(textMotionPresetById(presetId, motionPresets))
        : null
      let entryLeadSec = 0
      let exitTailSec = 0
      if (motionPreset) {
        const offsetSources = [motionPreset.offsets]
        Object.values(motionPreset.props || {}).forEach((g) => {
          if (g?.offsets) offsetSources.push(g.offsets)
        })
        offsetSources.forEach((o) => {
          const e = Number(o?.entry)
          const x = Number(o?.exit)
          if (Number.isFinite(e)) entryLeadSec = Math.max(entryLeadSec, Math.max(0, e))
          if (Number.isFinite(x)) exitTailSec = Math.max(exitTailSec, Math.max(0, x))
        })
      }

      // Map phrase source times → timeline times through the strip's window.
      const stripStart = Number(strip.start) || 0
      const sourceStart = Number(strip.sourceStart) || 0
      const rawScale = Number(strip.timeScale)
      const timeScale = rawScale > 0 ? rawScale : 1
      const stripLen = Math.max(0, Number(strip.length) || 0)
      const MIN_LEN = 0.05
      const TAIL_SEC = 0.3
      const GAP_PAD_SEC = 0.02

      const segments = []
      for (let i = 0; i < phrases.length; i++) {
        const p = phrases[i]
        const localStart = (p.start - sourceStart) / timeScale
        const localEnd = (p.end - sourceStart) / timeScale
        if (localEnd <= 0 || localStart >= stripLen) continue
        const next = phrases[i + 1]
        const nextLocalStart = next ? (next.start - sourceStart) / timeScale : null

        // Core (non-overlapping) placement: starts at the landing, short
        // tail clamped against the next phrase. Used directly for "no
        // motion", and as the fallback when the stagger lane cap is hit.
        const landing = stripStart + Math.max(0, localStart)
        let coreLength = (localEnd - localStart) + TAIL_SEC
        if (nextLocalStart !== null) {
          coreLength = Math.min(coreLength, Math.max(MIN_LEN, nextLocalStart - localStart - GAP_PAD_SEC))
        }
        coreLength = Math.max(MIN_LEN, Math.min(coreLength, Math.max(MIN_LEN, stripLen - localStart)))

        const seg = {
          text: p.text,
          wordCount: p.words.length,
          landing,
          coreTime: landing,
          coreLength,
        }
        if (motionPreset) {
          // Animated placement: lead in before the landing (clamped at
          // timeline zero), hold through the phrase, exit in the tail.
          const lead = Math.min(entryLeadSec, landing)
          const tail = Math.max(exitTailSec, TAIL_SEC)
          seg.time = landing - lead
          seg.length = Math.max(MIN_LEN, lead + Math.max(0, localEnd - localStart) + tail)
        } else {
          seg.time = landing
          seg.length = coreLength
        }
        segments.push(seg)
      }

      // Phase 1 test helper: inspect segmentation in the console.
      console.groupCollapsed(
        `[captions] ${segments.length} phrases ` +
          `(gap ${(gapSec * 1000).toFixed(0)}ms, sensitivity ${sensitivity.toFixed(2)})` +
          `${dryRun ? ' — dry run' : ''}`
      )
      console.table(
        segments.map((s) => ({
          phrase: s.text,
          start: Number(s.time.toFixed(3)),
          landing: Number(s.landing.toFixed(3)),
          length: Number(s.length.toFixed(3)),
          words: s.wordCount,
        }))
      )
      console.groupEnd()

      if (dryRun) return { success: true, segments, dryRun: true }
      if (!segments.length) return { success: false, error: 'NO_PHRASES' }

      // Target layer track: same preference logic as manual text entry.
      const tracks = store.state.tracks || {}
      const preferred = fromSession('ui/text/insertTrack', null)
      const isLayer = (id) => !!(tracks[id] && tracks[id].type === 'layer')
      const targetTrack = preferred && isLayer(preferred)
        ? preferred
        : Object.keys(tracks).find(isLayer)
      if (!targetTrack) return { success: false, error: 'NO_LAYER_TRACK' }

      // One undo step for the whole generation.
      if (!skipUndo) store.commit('_pushUndo', 'generateCaptions')

      // Stagger: overlapping animation windows alternate between the lowest
      // free lanes (above/below pattern), capped at this many lanes. When the
      // cap is hit, fall back to the core (non-overlapping) placement.
      const CAPTION_MAX_LANES = 3
      const baseDepth = Math.max(0, Number(store.state.tracks?.[targetTrack]?.depth) || 0)

      let created = 0
      let lastStripUuid = null
      for (const seg of segments) {
        const auuid = await store.dispatch('addText', {
          text: seg.text,
          thumbnail: null,
        })
        if (!auuid) continue
        let segTime = seg.time
        let segLength = seg.length
        let depth = pickCaptionLane({
          state: store.state,
          tuuid: targetTrack,
          time: segTime,
          length: segLength,
          baseDepth,
          maxLanes: CAPTION_MAX_LANES,
        })
        if (depth === null) {
          // All capped lanes busy: drop the animation runway and sit at the
          // core placement instead of stacking higher.
          segTime = seg.coreTime
          segLength = seg.coreLength
          depth = pickCaptionLane({
            state: store.state,
            tuuid: targetTrack,
            time: segTime,
            length: segLength,
            baseDepth,
            maxLanes: CAPTION_MAX_LANES,
          })
        }
        if (depth === null) {
          // Last resort (e.g. collision with pre-existing user strips):
          // insert above everything rather than overlap.
          depth = pickInsertDepth({
            state: store.state,
            tuuid: targetTrack,
            time: segTime,
            length: segLength,
          })
        }
        store.commit('addStrip', {
          auuid,
          tuuid: targetTrack,
          time: segTime,
          depth,
          length: segLength,
        })
        const newStripUuid = store.state.timeline.activeStrip
        if (newStripUuid) {
          // Tag for future regeneration/replacement (Phase 5).
          store.commit('setStripProp', {
            tuuid: targetTrack,
            suuid: newStripUuid,
            code: 'captionSource',
            value: { stripUuid: suuid, assetUuid: strip.asset?.uuid || null },
          })
          // Phase 5a: apply the chosen motion preset through the same
          // machinery the TextModal/AI agent use (smart groups only).
          if (presetId) {
            const createdStrip = store.state.tracks?.[targetTrack]?.strips?.[newStripUuid]
            const groups = extractTextAnimationGroups({
              operations: [
                { target: 'animation', action: 'upsert', payload: { presetId } },
              ],
              strip: createdStrip,
              asset: factory.asset(auuid),
              writeMode: 'merge_text_animation_ops',
              fps: finitePositive(store.state.settings?.fps, 24),
              globalTextMotionPresets:
                store.state.globalTextMotionPresets || GLOBAL_TEXT_MOTION_PRESETS,
            })
            if (groups && Object.keys(groups).length) {
              applyTextAnimationGroups({
                store,
                tuuid: targetTrack,
                suuid: newStripUuid,
                groups,
              })
            }
          }
          lastStripUuid = newStripUuid
        }
        created++
      }

      if (lastStripUuid) {
        store.commit('activeTrack', targetTrack)
        store.commit('activeStrip', lastStripUuid)
        store.commit('saved', false)
        store.commit('updateProjectLength')
      }

      return { success: true, created, track: targetTrack }
    },
    // Transcript warp Phase 5: set per-word gain (dB) on an audio strip.
    // start/end are the word's source-time range; gainDb ~0 removes the entry.
    setStripWordGain: (store, { tuuid, suuid, start, end, gainDb } = {}) => {
      const strip = store.state.tracks?.[tuuid]?.strips?.[suuid]
      if (!strip) return
      const s = Number(start)
      const e = Number(end)
      const db = Number(gainDb)
      if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return
      const EPS = 1e-3
      const prev = Array.isArray(strip.volumeAutomation) ? strip.volumeAutomation : []
      const next = prev.filter(
        (r) => !(Math.abs(Number(r?.start) - s) < EPS && Math.abs(Number(r?.end) - e) < EPS)
      )
      if (Number.isFinite(db) && Math.abs(db) > 0.05) {
        next.push({ start: s, end: e, gainDb: db })
      }
      next.sort((a, b) => a.start - b.start)
      store.commit('setStripProp', {
        tuuid,
        suuid,
        code: 'volumeAutomation',
        value: next,
      })
      store.commit('saved', false)
    },
    play: async (store) => {
      // console.log('play', store.state.play)
      // if (!store.state.tuned)
      // await store.dispatch('checkPlaySpeed')
      store.commit('play', true)
    },
    pause: (store) => {
      // console.log('pause', store.state.play)
      store.commit('play', false)
    },
    doRender: (store) => {
      store.commit('record', true)
    },
    stopRender: (store) => {
      store.commit('record', false)
    },
    launchEditorAiTool: async (store, { toolId = null, openPanel = true } = {}) => {
      const nextToolId = `${toolId || ''}`.trim()
      if (!nextToolId) return { ok: false, reason: 'missing_tool_id' }
      store.commit('aiTask', null)
      if (openPanel) {
        await store.dispatch('cmd/openPanel', { panel: 'vyby' }, { root: true })
      }
      store.commit('aiTool', nextToolId)
      return { ok: true, toolId: nextToolId }
    },
    launchEditorEffectSelection: async (
      store,
      { stripId = null, effectCode = null, preset = null, time = 0, openEffectsView = true } = {}
    ) => {
      const targetStripId = `${stripId || store.state.timeline.activeStrip || ''}`.trim()
      const nextEffectCode = `${effectCode || preset?.group || ''}`.trim()
      debugEffectDrop('launchEditorEffectSelection:input', {
        stripId: targetStripId || null,
        effectCode: nextEffectCode || null,
        presetGroup: preset?.group || null,
        time,
      })
      if (!targetStripId) {
        return { ok: false, reason: 'missing_target_strip' }
      }
      if (!nextEffectCode) {
        return { ok: false, reason: 'missing_effect_code' }
      }

      const hostTarget = await store.dispatch('resolveEffectHostTarget', {
        stripId: targetStripId,
        effectCode: nextEffectCode,
        preset: preset || null,
      })
      debugEffectDrop('launchEditorEffectSelection:host-target', hostTarget || null)
      if (!hostTarget?.ok) return hostTarget

      const finalTrackId = hostTarget.tuuid || null
      const finalStripId = hostTarget.targetStripId || targetStripId
      if (!finalTrackId || !finalStripId) {
        return { ok: false, reason: 'missing_resolved_effect_target' }
      }

      const requestedTime = Math.max(0, Number(time) || 0)
      const resolvedStrip = hostTarget.strip || null
      const effectTime = hostTarget.adjustmentHostCreated ? 0 : requestedTime
      const span = Math.max(0, Number(resolvedStrip?.length) || 0)

      await store.dispatch('addEffect', {
        type: preset ? 'effect-preset' : 'effect',
        effect: nextEffectCode,
        preset: preset || null,
        track: finalTrackId,
        strip: finalStripId,
        time: effectTime,
        span: span || undefined,
      })
      debugEffectDrop('launchEditorEffectSelection:add-effect', {
        trackId: finalTrackId,
        stripId: finalStripId,
        effectCode: nextEffectCode,
        effectTime,
        span: span || null,
      })

      store.commit('activeTrack', finalTrackId)
      store.commit('activeStrip', finalStripId)
      if (openEffectsView) store.commit('effectsView', true)

      return {
        ok: true,
        trackId: finalTrackId,
        stripId: finalStripId,
        adjustmentHostCreated: !!hostTarget.adjustmentHostCreated,
      }
    },
    addEffect: (store, data) => {
      const track = store.state.tracks[data.track]
      if (!track) return
      const strip = track.strips[data.strip]
      if (!strip) return
      // console.log('@addEffect', data)

      const effect = data.type === 'effect-preset'
        ? factory.createEffectPreset(data.preset, {
          bpm: store.state.settings.bpm,
          strip: data.strip,
          time: data.time,
          type: data.type,
          // pass through link fields when provided
          span: typeof data.span === 'number' ? data.span : undefined,
          linkedStrip: data.linkedStrip,
          scene: data.scene
        })
        : factory.createEffect(data, {
          bpm: store.state.settings.bpm,
          time: data.time,
          type: data.type,
          span: typeof data.span === 'number' ? data.span : undefined,
          linkedStrip: data.linkedStrip,
          scene: data.scene,
          ...(isPlainObject(data.initialData) ? data.initialData : {}),
        })

      if (!effect) return

      // console.log('ADD EFFECT', effect, effect.isPreset)
      store.commit('addEffect', {
        tuuid: data.track,
        suuid: data.strip,
        effect,
      })
      store.commit('saved', false)
    },
    addAdjustmentLayer: (store, { track = null, time = null, length = null } = {}) => {
      const tuuid = (track && store.state.tracks[track] && store.state.tracks[track].type === 'layer')
        ? track
        : (store.state.timeline.activeTrack && store.state.tracks[store.state.timeline.activeTrack]?.type === 'layer')
          ? store.state.timeline.activeTrack
          : tracksIndex.trackUuid1
      const t = Number.isFinite(time) ? time : (Number(store.state.timeline.currentTime) || 0)
      const len = Number.isFinite(length) && length > 0 ? length : DEFAULT_DURATION

      const asset = factory.createAdjustment()
      const tr = store.state.tracks[tuuid]
      const strips = Object.values(tr?.strips || {})
      // Find a free depth lane for this time span on the chosen track.
      let depth = Math.max(0, Number(tr?.maxDepth) || 0)
      for (let d = depth; d < depth + 20; d++) {
        const ok = !strips.some((s) => s && !stripCanDrop(d, t, len, s))
        if (ok) {
          depth = d
          break
        }
      }

      store.commit('_pushUndo', 'addAdjustmentLayer')
      store.commit('addStrip', { auuid: asset.uuid, tuuid, time: t, depth, length: len })
      store.commit('saved', false)
      store.commit('updateProjectLength')
      return store.state.timeline.activeStrip
    },
    addAdjustmentLayerOverStrip: async (store, { suuid, preset = null } = {}) => {
      const host = createAdjustmentHostOverStrip(store, { suuid })
      if (!host?.stripId) return null

      await store.dispatch('project/addEffect', {
        type: preset ? 'effect-preset' : 'effect',
        effect: 'postprocessing',
        preset,
        track: host.tuuid,
        strip: host.stripId,
        time: 0,
        span: host.length
      })

      return host.stripId
    },
    resolveEffectHostTarget: async (
      store,
      { stripId = null, effectItems = null, effectCode = null, preset = null } = {}
    ) => {
      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          ok: false,
          reason: 'target_strip_not_found',
          stripId: stripId || null,
        }
      }

      const needsAdjustmentHost =
        isMediaEffectTargetType(stripBaseType(location.strip)) &&
        requestRequiresAdjustmentHost({ effectItems, effectCode, preset })
      debugEffectDrop('resolveEffectHostTarget:input', {
        stripId,
        trackId: location.tuuid,
        stripType: stripBaseType(location.strip),
        effectCode,
        presetGroup: preset?.group || null,
        needsAdjustmentHost,
      })

      if (!needsAdjustmentHost) {
        return {
          ok: true,
          targetStripId: stripId,
          requestedTargetStripId: null,
          adjustmentHostCreated: false,
          tuuid: location.tuuid,
          strip: location.strip,
        }
      }

      const host = createAdjustmentHostOverStrip(store, { suuid: stripId })
      if (!host?.stripId) {
        return {
          ok: false,
          reason: 'adjustment_host_resolution_failed',
          stripId,
        }
      }

      const hostLocation = findStripLocation(store.state, host.stripId)
      if (!hostLocation) {
        return {
          ok: false,
          reason: 'adjustment_host_not_found',
          stripId: host.stripId,
        }
      }

      return {
        ok: true,
        targetStripId: host.stripId,
        requestedTargetStripId: stripId,
        adjustmentHostCreated: true,
        tuuid: hostLocation.tuuid,
        strip: hostLocation.strip,
      }
    },
    // linkPpeToClip removed in Phase 3 (adjustment layers replace scene-name routing)
    saveEffectPreset: (store, data) => {
      const preset = createEffectUserPreset({
        ..._pick(data, ['name', 'group', 'img']),
        id: data.preset.uuid,
        preset: _omit(data.preset, ['object', 'uuid']),
      })
      store.commit('upsertEffectPreset', preset)
      const { tuuid, suuid } = data
      if (!tuuid || !suuid) return
      const sourceEffect = factory.effect(data.preset.uuid) || {}
      const replacePreset = factory.createEffectPreset(preset, {
        bpm: store.state.settings.bpm,
        strip: suuid,
        time: sourceEffect.time || 0,
      })
      console.log('REPLACE', sourceEffect, replacePreset, data)
      if (sourceEffect && replacePreset) {
        store.commit('removeEffect', {
          suuid,
          euuid: sourceEffect.uuid,
        })
        store.commit('addEffect', {
          suuid,
          effect: replacePreset,
        })
      }
    },
    buildTransformAgentState: (store, { stripId = null, frameRange = null } = {}) =>
      buildTransformAgentState({
        projectState: store.state,
        stripId,
        frameRange,
      }),
    toggleClipRangeEdit: (store, { stripId = null, active = undefined } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      const currentActive =
        !!store.state.clipRangeEdit?.active &&
        store.state.clipRangeEdit?.stripId === targetStripId
      const nextActive =
        typeof active === 'boolean' ? active : !currentActive
      store.commit('clipRangeEdit', {
        active: nextActive,
        stripId: nextActive ? targetStripId : null,
      })
    },
    setActiveStripClipRange: (store, { range, enabled = true } = {}) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('setStripClipRange', { tuuid, suuid, range, enabled })
    },
    setActiveStripClipRangeLive: (store, { range, enabled = true } = {}) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('setStripClipRangeLive', { tuuid, suuid, range, enabled })
    },
    clearActiveStripClipRange: (store) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('clearStripClipRange', { tuuid, suuid })
    },
    openSubclipModal: (store, { stripId = null, source = null, payload = null } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      store.commit('subclipModal', {
        open: true,
        stripId: targetStripId,
        source,
        payload,
      })
    },
    closeSubclipModal: (store) => {
      store.commit('subclipModal', {
        open: false,
        stripId: null,
        source: null,
        payload: null,
      })
    },
    openTextModal: (store, { stripId = null, source = null } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      store.commit('textModal', {
        open: true,
        stripId: targetStripId,
        source,
      })
    },
    closeTextModal: (store) => {
      store.commit('textModal', {
        open: false,
        stripId: null,
        source: null,
      })
    },
    clearSubclipModalResult: (store) => {
      store.commit('subclipModalResult', null)
    },
    queueNodeClipSpawn: (store, payload = {}) => {
      const assetUuids = Array.isArray(payload.assetUuids)
        ? payload.assetUuids
            .map((uuid) => `${uuid || ''}`.trim())
            .filter(Boolean)
            .slice(0, 4)
        : []
      if (!assetUuids.length) return null
      const id = Uuid()
      store.commit('nodeClipSpawnRequest', {
        ...payload,
        id,
        assetUuids,
      })
      return id
    },
    clearNodeClipSpawnRequest: (store, { id = null } = {}) => {
      const currentId = store.state.nodeClipSpawnRequest?.id || null
      if (id && currentId && id !== currentId) return false
      store.commit('nodeClipSpawnRequest', null)
      return true
    },
    buildTransformAgentHandoff: (
      store,
      {
        task = {},
        policy = {},
        stripId = null,
        frameRange = null,
      } = {}
    ) => {
      const state = buildTransformAgentState({
        projectState: store.state,
        stripId,
        frameRange,
      })
      if (!state) return null

      return {
        agent: 'transform',
        task: {
          goal: task.goal || '',
          intent: task.intent || 'hold',
          speedProfile: task.speedProfile || 'medium',
          intensity: task.intensity || 'normal',
          startPolicy: task.startPolicy || 'current_frame',
          rangePolicy: task.rangePolicy || 'selected_range',
          overwriteIntent: task.overwriteIntent || 'merge',
          ...(task.notesFromPlanner
            ? { notesFromPlanner: task.notesFromPlanner }
            : {}),
        },
        state,
        policy: {
          framingPolicy: policy.framingPolicy || 'center_safe',
          allowBorderExposure: !!policy.allowBorderExposure,
          allowReplaceAllTransformKeys: !!policy.allowReplaceAllTransformKeys,
          preferMinimalKeyframes:
            typeof policy.preferMinimalKeyframes === 'boolean'
              ? policy.preferMinimalKeyframes
              : true,
          ...(typeof policy.allowCompoundMotion === 'boolean'
            ? { allowCompoundMotion: policy.allowCompoundMotion }
            : {}),
          ...(typeof policy.allowRotation === 'boolean'
            ? { allowRotation: policy.allowRotation }
            : {}),
          ...(Number.isFinite(Number(policy.safeScaleFloorPct))
            ? { safeScaleFloorPct: Number(policy.safeScaleFloorPct) }
            : {}),
          ...(Number.isFinite(Number(policy.maxKeyframes))
            ? { maxKeyframes: Number(policy.maxKeyframes) }
            : {}),
        },
      }
    },
    previewTransformAgentMotion: (
      store,
      {
        task = {},
        policy = {},
        stripId = null,
        frameRange = null,
      } = {}
    ) => {
      const handoff = actions.buildTransformAgentHandoff(store, {
        task,
        policy,
        stripId,
        frameRange,
      })
      if (!handoff) return null

      const effectiveRange = resolveTransformFrameRange({
        state: handoff.state,
        startPolicy: handoff.task.startPolicy,
        rangePolicy: handoff.task.rangePolicy,
      })
      if (!effectiveRange) return { ...handoff, effectiveRange: null, motion: null }

      const startFrameTransform = buildTransformAtFrame({
        projectState: store.state,
        stripId: handoff.state.selectedStripId,
        frame: effectiveRange.startFrame,
      })

      return {
        ...handoff,
        effectiveRange,
        startFrameTransform,
        motion: resolveTransformMotionPreview({
          task: handoff.task,
          frameSpan: effectiveRange.frameSpan,
          fps: store.state.settings.fps,
          currentTransform: startFrameTransform,
          transformMode: handoff.state.transformMode,
          policy: handoff.policy,
        }),
      }
    },
    applyTransformAgentResult: (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload' }
      }
      if (payload.schema !== 'transform.set_keyframes.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId
          ? payload.selectedStripId
          : null
      const tuuid = stripId
        ? Object.keys(store.state.tracks || {}).find(
          (id) => !!store.state.tracks?.[id]?.strips?.[stripId]
        )
        : null
      const strip = tuuid ? store.state.tracks?.[tuuid]?.strips?.[stripId] : null
      const stripAsset =
        strip && strip.asset?.uuid ? factory.asset(strip.asset.uuid) : null
      const runtimeRefreshEmitterFound = !!(
        strip &&
        stripAsset &&
        typeof stripAsset.emit === 'function'
      )

      store.commit('applyTransformAgentSetKeyframes', { payload, source })
      return {
        applied: true,
        schema: payload.schema,
        stripId: payload.selectedStripId || null,
        writeMode: payload.writeMode || null,
        keyframeCount: Array.isArray(payload.keyframes) ? payload.keyframes.length : 0,
        runtimeRefreshEmitterFound,
      }
    },
    applyTextAnimationAgentResult: async (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload', source }
      }
      if (payload.schema !== 'text_animation.apply.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema, source }
      }

      const mode = `${payload.mode || ''}`.trim().toLowerCase()
      const operations = Array.isArray(payload.operations) ? payload.operations : []
      const writeMode = `${payload.writeMode || 'merge_text_animation_ops'}`.trim()
      const fps = finitePositive(store.state.settings?.fps, 24)
      const playheadSec = finiteNonNegative(store.state.timeline?.currentTime, 0)
      const contentFromOps = extractTextFromTextAnimationOperations(operations)
      if (!TEXT_ANIMATION_AGENT_WRITE_MODES.has(writeMode)) {
        return {
          applied: false,
          reason: 'unsupported_text_animation_write_mode',
          writeMode,
          schema: payload.schema,
          source,
        }
      }
      const operationValidation = validateTextAnimationAgentOperations(operations)
      if (!operationValidation.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...operationValidation,
        }
      }

      if (mode === 'create') {
        const create = isPlainObject(payload.create) ? payload.create : {}
        const text = firstNonEmptyString(
          create.text,
          create.content,
          create.value,
          contentFromOps
        )
        if (!text) {
          return {
            applied: false,
            reason: 'missing_text_content',
            schema: payload.schema,
            source,
          }
        }

        const tuuid = pickTextCreateTrackId(store.state)
        if (!tuuid) {
          return {
            applied: false,
            reason: 'no_layer_track_available',
            schema: payload.schema,
            source,
          }
        }

        const startSec = extractTextCreateStartSec({
          payload,
          timelinePlayheadSec: playheadSec,
          projectFps: fps,
        })
        const durationSec = extractTextCreateDurationSec({
          payload,
          operations,
          projectFps: fps,
        })
        const rangeResolution = resolveTextAnimationRangeSeconds({
          payload,
          strip: { length: durationSec },
          fps,
        })
        if (!rangeResolution.ok) {
          return {
            applied: false,
            schema: payload.schema,
            source,
            ...rangeResolution,
          }
        }

        const auuid = await store.dispatch('addText', { text, thumbnail: null })
        if (!auuid) {
          return {
            applied: false,
            reason: 'text_asset_create_failed',
            schema: payload.schema,
            source,
          }
        }

        const depth = pickInsertDepth({
          state: store.state,
          tuuid,
          time: startSec,
          length: durationSec,
        })
        store.commit('addStrip', {
          auuid,
          tuuid,
          time: startSec,
          depth,
          length: durationSec,
        })
        const createdStripId = store.state.timeline.activeStrip || null
        store.commit('saved', false)
        store.commit('updateProjectLength')

        const createdParts = ['content', 'timing']
        const createdLocation = findStripLocation(store.state, createdStripId)
        if (createdLocation) {
          const createdAsset = factory.asset(auuid)
          const styleUpdates = extractTextStyleOperations(operations)
          if (applyTextStyleUpdates({
            strip: createdLocation.strip,
            asset: createdAsset,
            updates: styleUpdates,
          })) {
            createdParts.push('style')
          }
          const animationGroups = extractTextAnimationGroups({
            operations,
            strip: createdLocation.strip,
            asset: createdAsset,
            writeMode,
            rangeSeconds: rangeResolution.range,
            fps,
            globalTextMotionPresets: store.state.globalTextMotionPresets,
          })
          if (applyTextAnimationGroups({
            store,
            tuuid: createdLocation.tuuid,
            suuid: createdStripId,
            groups: animationGroups,
          })) {
            createdParts.push('animation')
          }
        }

        return {
          applied: true,
          schema: payload.schema,
          mode,
          stripId: createdStripId,
          trackId: tuuid,
          durationSec,
          source,
          appliedParts: createdParts,
          message: `Created text strip (${durationSec.toFixed(2)}s).`,
        }
      }

      if (mode !== 'edit') {
        return {
          applied: false,
          reason: 'unsupported_mode',
          mode: payload.mode || null,
          schema: payload.schema,
          source,
        }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId.trim()
          ? payload.selectedStripId.trim()
          : null
      if (!stripId) {
        return {
          applied: false,
          reason: 'needs_context:selectedStripId',
          missing: ['selectedStripId'],
          schema: payload.schema,
          source,
        }
      }

      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          applied: false,
          reason: 'target_strip_not_found',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      const { tuuid, strip } = location
      if (`${strip.asset?.type || ''}`.toLowerCase() !== 'text') {
        return {
          applied: false,
          reason: 'target_strip_not_text',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      const rangeResolution = resolveTextAnimationRangeSeconds({ payload, strip, fps })
      if (!rangeResolution.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...rangeResolution,
        }
      }

      const appliedParts = []
      if (contentFromOps) {
        await store.dispatch('editText', {
          auuid: strip.asset.uuid,
          text: contentFromOps,
          thumbnail: null,
        })
        appliedParts.push('content')
      }

      const timing = extractTextTimingFromOperations(operations, fps)
      const nextStart = finiteNonNegative(timing.startSec, finiteNonNegative(strip.start, 0))
      const nextLength = finitePositive(timing.durationSec, finitePositive(strip.length, DEFAULT_DURATION))
      const startChanged = Math.abs(nextStart - finiteNonNegative(strip.start, 0)) > 1e-6
      const lengthChanged = Math.abs(nextLength - finitePositive(strip.length, DEFAULT_DURATION)) > 1e-6
      if (startChanged || lengthChanged) {
        store.commit('activeTrack', tuuid)
        store.commit('activeStrip', stripId)
        await store.dispatch('trimActiveStrip', { start: nextStart, length: nextLength })
        appliedParts.push('timing')
      }

      const textAsset = factory.asset(strip.asset.uuid)
      const styleUpdates = extractTextStyleOperations(operations)
      if (applyTextStyleUpdates({ strip, asset: textAsset, updates: styleUpdates })) {
        appliedParts.push('style')
      }

      const animationGroups = extractTextAnimationGroups({
        operations,
        strip,
        asset: textAsset,
        writeMode,
        rangeSeconds: rangeResolution.range,
        fps,
        globalTextMotionPresets: store.state.globalTextMotionPresets,
      })
      if (applyTextAnimationGroups({
        store,
        tuuid,
        suuid: stripId,
        groups: animationGroups,
      })) {
        appliedParts.push('animation')
      }

      if (!appliedParts.length) {
        return {
          applied: false,
          reason: 'no_supported_operations',
          stripId,
          schema: payload.schema,
          source,
        }
      }

      store.commit('saved', false)
      return {
        applied: true,
        schema: payload.schema,
        mode,
        stripId,
        source,
        appliedParts,
        message: `Updated text strip (${appliedParts.join(', ')}).`,
      }
    },
    applyEffectsAgentResult: async (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload', source }
      }
      if (payload.schema !== 'effects.apply.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema, source }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId.trim()
          ? payload.selectedStripId.trim()
          : null
      if (!stripId) {
        return {
          applied: false,
          reason: 'needs_context:selectedStripId',
          missing: ['selectedStripId'],
          schema: payload.schema,
          source,
        }
      }

      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          applied: false,
          reason: 'target_strip_not_found',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      let targetStripId = stripId
      let { tuuid, strip } = location
      const effectItems = Array.isArray(payload.effects) ? payload.effects : []
      const writeMode = `${payload.writeMode || 'merge_effect_controls'}`.trim()
      if (!effectItems.length) {
        return {
          applied: false,
          reason: 'no_effect_operations',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      if (!EFFECTS_AGENT_WRITE_MODES.has(writeMode)) {
        return {
          applied: false,
          reason: 'unsupported_effect_write_mode',
          writeMode,
          schema: payload.schema,
          source,
        }
      }

      const validation = validateEffectsAgentItems(effectItems)
      if (!validation.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...validation,
        }
      }

      const fps = finitePositive(store.state.settings?.fps, 24)
      const rangeResolution = resolveEffectsAgentRangeSeconds({ payload, strip, fps })
      if (!rangeResolution.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...rangeResolution,
        }
      }
      const rangeSeconds = rangeResolution.range

      const hostTarget = await store.dispatch('resolveEffectHostTarget', {
        stripId,
        effectItems,
      })
      if (!hostTarget?.ok) {
        return {
          applied: false,
          reason: hostTarget?.reason || 'adjustment_host_resolution_failed',
          stripId: hostTarget?.stripId || targetStripId,
          schema: payload.schema,
          source,
        }
      }
      targetStripId = hostTarget.targetStripId || targetStripId
      tuuid = hostTarget.tuuid || tuuid
      strip = hostTarget.strip || strip
      const adjustmentHostCreated = !!hostTarget.adjustmentHostCreated
      const requestedTargetStripId = hostTarget.requestedTargetStripId || null

      let upserted = 0
      let removed = 0
      let skipped = 0

      for (const effectItem of effectItems) {
        if (!isPlainObject(effectItem)) {
          skipped++
          continue
        }
        const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
        const effectCode = normalizeEffectCode(effectItem.effectCode)
        const shouldCreateNew =
          action === 'upsert' &&
          (writeMode === 'append_effect_controls' ||
            writeMode === 'replace_all_effect_controls')
        if (writeMode === 'replace_all_effect_controls' && action === 'upsert') {
          removed += removeMatchingEffectsForAgentItem(store, {
            tuuid,
            suuid: targetStripId,
            strip,
            effectItem,
          })
          strip = store.state.tracks?.[tuuid]?.strips?.[targetStripId] || strip
        }
        const resolvedEffectId = shouldCreateNew
          ? null
          : resolveStripEffectId({
              strip,
              effectId: effectItem.effectId,
              effectCode,
              math: effectItemMath(effectItem),
            })

        if (action === 'remove') {
          if (!resolvedEffectId) {
            skipped++
            continue
          }
          store.commit('removeEffect', {
            tuuid,
            suuid: targetStripId,
            euuid: resolvedEffectId,
          })
          removed++
          continue
        }

        const patch = withEffectRangePatch(buildEffectUpdatePatch(effectItem), rangeSeconds)
        if (resolvedEffectId) {
          if (Object.keys(patch).length) {
            store.commit('updateEffect', {
              tuuid,
              suuid: targetStripId,
              euuid: resolvedEffectId,
              data: patch,
            })
          }
          upserted++
          continue
        }

        if (!effectCode) {
          skipped++
          continue
        }

        await store.dispatch('addEffect', {
          type: 'effect',
          effect: effectCode,
          track: tuuid,
          strip: targetStripId,
          time: rangeSeconds ? rangeSeconds.startSec : 0,
          span: rangeSeconds ? rangeSeconds.spanSec : undefined,
          initialData: patch,
        })
        const createdEffectId =
          typeof store.state.timeline.activeEffect === 'string'
            ? store.state.timeline.activeEffect
            : null
        if (!createdEffectId) {
          skipped++
          continue
        }

        if (Object.keys(patch).length) {
          store.commit('updateEffect', {
            tuuid,
            suuid: targetStripId,
            euuid: createdEffectId,
            data: patch,
          })
        }
        upserted++
      }

      const appliedCount = upserted + removed
      if (!appliedCount) {
        return {
          applied: false,
          reason: 'no_applicable_effect_changes',
          stripId: targetStripId,
          schema: payload.schema,
          source,
          skipped,
        }
      }

      store.commit('activeTrack', tuuid)
      store.commit('activeStrip', targetStripId)
      store.commit('effectsView', true)
      store.commit('saved', false)
      return {
        applied: true,
        schema: payload.schema,
        stripId: targetStripId,
        source,
        upserted,
        removed,
        skipped,
        adjustmentHostCreated,
        requestedTargetStripId,
        message: adjustmentHostCreated
          ? `Created adjustment host and applied effects (${upserted} upserted, ${removed} removed).`
          : `Applied effects changes (${upserted} upserted, ${removed} removed).`,
      }
    },
    // calculations
    checkPlaySpeed: async (store) => {
      store.commit('calculate', true)
      const ts = store.state.timeline.currentTime
      const testFrom = ts + 1 < store.state.timeline.length ? ts : ts - 1
      if (testFrom < 0) return 1
      store.commit('currentTime', testFrom)
      store.commit('tune', 1)
      const start = new Date().getTime()
      store.commit('play', true)
      await waitMs(1000)
      const end = new Date().getTime()
      store.commit('play', false)
      const testTo = store.state.timeline.currentTime
      store.commit('currentTime', ts)
      store.commit('calculate', false)
      const tune = (end - start) / ((testTo - testFrom) * 1000)
      store.commit('tune', tune)
      store.commit('tuned', true)
      console.log('TEST RESULTS', testTo - testFrom, end - start, tune)
    },
  }

export default actions
